import { AxisGridRenderer } from "../canvas/AxisGridRenderer";
import { AxisGridRenderCache, buildAxisGridCacheKey } from "../canvas/AxisGridRenderCache";
import { BarRenderer } from "../canvas/BarRenderer";
import { CandlestickRenderer, toHeikinAshiCandles } from "../canvas/CandlestickRenderer";
import { CrosshairRenderer } from "../canvas/CrosshairRenderer";
import { PriceLineRenderer } from "../canvas/PriceLineRenderer";
import { PriceLineOverlay, resolvePriceSlotSide } from "./priceLineOverlay";
import { PriceScaleController } from "./priceScaleController";
import { resolveCandleStyleColors } from "./candleStyleColors";
import { axisGridThemeInputs } from "./axisGridTheme";
import { createPaneProjector } from "../coordinates/paneProjector";
import { scaleValueToRatio } from "../coordinates/priceScale";
import { readOhlc, snapCrosshairYToOHLC, zoomDomainByPixels, } from "../coordinates/crosshairMagnet";
import { resolvePaneDataIndexAtPixel, resolvePanePixelAtDataIndex, resolvePanePixelAtValue, resolvePaneValueAtPixel } from "../coordinates/coordinateBridge";
import { LineAreaRenderer } from "../canvas/LineAreaRenderer";
import { isColumnStackMode, isPriceLevelMode, isRectangleMode, isRenkoMode, isStepMode, isZeroFlooredMode, resolveRectangleBaselines, resolveRectanglePalette, resolveSeriesRenderMode, } from "./exoticSeriesModes";
import { layoutPointAndFigure, PNF_UP, widenDomainForPointAndFigure } from "./pointAndFigureColumns";
import { PaneLayoutEngine, clonePaneRuntimeSnapshot } from "../pane/PaneLayoutEngine";
import { MAX_VISIBLE_BARS, MIN_VISIBLE_BARS, RIGHT_EDGE_ANCHOR_R, SAFE_MAX_BAR_WIDTH, SHARED_ENVELOPE_MAX_BAR_SPACE_PX, SHARED_ENVELOPE_MIN_BAR_SPACE_PX, ZOOM_K, } from "./zoomConstants";
import { UPSTREAM_BASELINE } from "../upstream";
import { DirtyFlag } from "./dirtyMask";
import { INDICATOR_ACCESSORS, getAccessorFor } from "./indicatorAccessors";
import { InteractionController } from "../interaction/InteractionController";
import { createDerivedCache, appendTailCache, prependHeadCache, replaceAllCache, queryYRange, } from "./derivedCache";
import { hitTestOverlayPrimitive } from "../overlay/canvasOverlayHitTest";
import { renderOverlayPrimitiveToCanvas } from "../overlay/canvasOverlayRenderer";
import { isPremiumDrawingBlocked, requiresAds, FREE_AD_TIERS } from "../patTier";
import { verifyLicenseKey } from "../licenseValidator";
import { aggregateOhlc, chooseLodBucketSize } from "./LodBucketizer";
import { CandlestickLodWorkerLane, toCandlestickLodData, toCandlestickLodWorkerInput } from "./CandlestickLodWorkerLane";
const DEFAULT_THEME = { mode: "dark", presetName: "classic-dark" };
const DEFAULT_VISIBLE_RANGE = { from: 0, to: 300 };
const DEFAULT_LOCALE = "vi";
const DEFAULT_WIDTH = 960;
const DEFAULT_HEIGHT = 540;
const CAMERA_RANGE_MAX_ABS_INDEX = 1_000_000;
/** Height reserved for the panel header area at the top of the chart. */
const PANEL_HEADER_HEIGHT = 28;
const DEFAULT_OVERLAY_INPUTS = {
    measurementEnabled: false,
    selectedPriceLabels: null,
    drawingPrimitives: null,
};
function clampUnit(value, fallback) {
    if (!Number.isFinite(value)) {
        return fallback;
    }
    return Math.max(0, Math.min(1, value));
}
function normalizeHexColor(input) {
    const hex8 = /^#([0-9a-fA-F]{8})$/;
    const hex6 = /^#([0-9a-fA-F]{6})$/;
    const hex3 = /^#([0-9a-fA-F]{3})$/;
    if (hex6.test(input)) {
        return input;
    }
    const match8 = input.match(hex8);
    if (match8) {
        return `#${match8[1].substring(0, 6)}`;
    }
    const match3 = input.match(hex3);
    if (!match3) {
        return null;
    }
    const expanded = match3[1]
        .split("")
        .map((char) => `${char}${char}`)
        .join("");
    return `#${expanded}`;
}
function withAlpha(color, alpha) {
    const safeAlpha = clampUnit(alpha, 0.2);
    const normalizedHex = normalizeHexColor(color);
    if (normalizedHex) {
        const channel = Math.round(safeAlpha * 255)
            .toString(16)
            .padStart(2, "0");
        return `${normalizedHex}${channel}`;
    }
    const rgbaMatch = color.match(/^rgba?\(([^)]+)\)$/i);
    if (rgbaMatch) {
        const parts = rgbaMatch[1].split(",").map((part) => part.trim());
        if (parts.length >= 3) {
            const r = Number.parseFloat(parts[0]);
            const g = Number.parseFloat(parts[1]);
            const b = Number.parseFloat(parts[2]);
            if ([r, g, b].every((channel) => Number.isFinite(channel))) {
                return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${safeAlpha})`;
            }
        }
    }
    return color;
}
function clampOverlayIndex(index, length) {
    if (length <= 0) {
        return 0;
    }
    const lastIndex = length - 1;
    return Math.min(lastIndex, Math.max(0, Math.floor(index)));
}
function cloneOverlayState(overlayState) {
    return {
        crosshair: overlayState.crosshair ? { ...overlayState.crosshair } : null,
        hoveredPaneId: overlayState.hoveredPaneId,
        hoveredDataIndex: overlayState.hoveredDataIndex,
        xAxisTicks: overlayState.xAxisTicks.map((tick) => ({ ...tick })),
        measurement: overlayState.measurement
            ? {
                enabled: overlayState.measurement.enabled,
                hoverPoint: overlayState.measurement.hoverPoint ? { ...overlayState.measurement.hoverPoint } : null,
                draft: overlayState.measurement.draft
                    ? {
                        ...overlayState.measurement.draft,
                        start: { ...overlayState.measurement.draft.start },
                        end: { ...overlayState.measurement.draft.end },
                    }
                    : null,
                committed: overlayState.measurement.committed
                    ? {
                        ...overlayState.measurement.committed,
                        start: { ...overlayState.measurement.committed.start },
                        end: { ...overlayState.measurement.committed.end },
                    }
                    : null,
            }
            : null,
        drawingPriceLabels: overlayState.drawingPriceLabels
            ? {
                ...overlayState.drawingPriceLabels,
                labels: overlayState.drawingPriceLabels.labels.map((label) => ({ ...label })),
            }
            : null,
        drawingPrimitives: overlayState.drawingPrimitives.map(cloneDrawingPrimitiveState),
    };
}
function cloneOverlayCanvasPoint(point) {
    return { ...point };
}
function cloneOverlayCanvasPrimitive(primitive) {
    return {
        ...primitive,
        points: primitive.points.map(cloneOverlayCanvasPoint),
        style: {
            ...primitive.style,
            dashPattern: primitive.style.dashPattern ? [...primitive.style.dashPattern] : undefined,
        },
        levelLines: primitive.levelLines?.map((line) => ({ ...line })),
        arcLevels: primitive.arcLevels?.map((level) => ({
            ...level,
            labelPosition: level.labelPosition ? cloneOverlayCanvasPoint(level.labelPosition) : undefined,
        })),
        verticalMarkers: primitive.verticalMarkers?.map((marker) => ({
            ...marker,
            labelPosition: marker.labelPosition ? cloneOverlayCanvasPoint(marker.labelPosition) : undefined,
        })),
        segments: primitive.segments?.map((segment) => ({
            ...segment,
            start: cloneOverlayCanvasPoint(segment.start),
            end: cloneOverlayCanvasPoint(segment.end),
            labelPosition: segment.labelPosition ? cloneOverlayCanvasPoint(segment.labelPosition) : undefined,
            dashPattern: segment.dashPattern ? [...segment.dashPattern] : undefined,
        })),
        paths: primitive.paths?.map((path) => ({
            ...path,
            points: path.points.map(cloneOverlayCanvasPoint),
        })),
        pointMarkers: primitive.pointMarkers?.map((marker) => ({
            ...marker,
            point: cloneOverlayCanvasPoint(marker.point),
        })),
        labels: primitive.labels?.map((label) => ({
            ...label,
            position: cloneOverlayCanvasPoint(label.position),
        })),
        polygons: primitive.polygons?.map((polygon) => ({
            ...polygon,
            points: polygon.points.map(cloneOverlayCanvasPoint),
        })),
        semanticBadges: primitive.semanticBadges?.map((badge) => ({
            ...badge,
            position: cloneOverlayCanvasPoint(badge.position),
        })),
    };
}
function cloneDrawingPrimitiveInput(input) {
    return {
        key: input.key,
        paneId: input.paneId,
        scaleSlotId: input.scaleSlotId,
        primitive: cloneOverlayCanvasPrimitive(input.primitive),
        selected: input.selected,
        drawingId: input.drawingId,
        actionHandle: input.actionHandle,
        clipToPane: input.clipToPane,
    };
}
function cloneDrawingPrimitiveState(state) {
    return {
        key: state.key,
        paneId: state.paneId,
        scaleSlotId: state.scaleSlotId,
        primitive: cloneOverlayCanvasPrimitive(state.primitive),
        selected: state.selected,
        drawingId: state.drawingId,
        actionHandle: state.actionHandle,
        clipToPane: state.clipToPane,
    };
}
function cloneOverlayInputs(inputs) {
    return {
        measurementEnabled: inputs.measurementEnabled,
        selectedPriceLabels: inputs.selectedPriceLabels
            ? {
                ...inputs.selectedPriceLabels,
                markers: inputs.selectedPriceLabels.markers.map((marker) => ({ ...marker })),
            }
            : null,
        drawingPrimitives: inputs.drawingPrimitives?.map(cloneDrawingPrimitiveInput) ?? null,
        drawingPrimitivesProvider: inputs.drawingPrimitivesProvider,
    };
}
function cloneLogicalMeasurementPoint(point) {
    return {
        paneId: point.paneId,
        scaleSlotId: point.scaleSlotId,
        index: point.index,
        price: point.price,
    };
}
function cloneLogicalMeasurementSelection(selection) {
    return {
        paneId: selection.paneId,
        scaleSlotId: selection.scaleSlotId,
        start: cloneLogicalMeasurementPoint(selection.start),
        end: cloneLogicalMeasurementPoint(selection.end),
    };
}
function resolveVisibleScaleSlotIds(snapshot) {
    return Object.entries(snapshot.scaleSlotStates)
        .filter(([, state]) => state.visible)
        .map(([scaleSlotId]) => scaleSlotId);
}
function resolvePreferredMeasurementScaleSlotId(snapshot, requestedScaleSlotId) {
    if (requestedScaleSlotId && snapshot.scaleSlotStates[requestedScaleSlotId]) {
        return requestedScaleSlotId;
    }
    const visibleScaleSlotIds = resolveVisibleScaleSlotIds(snapshot);
    if (visibleScaleSlotIds.length === 0) {
        return undefined;
    }
    return visibleScaleSlotIds.find((scaleSlotId) => snapshot.scaleSlotStates[scaleSlotId]?.side === "right")
        ?? visibleScaleSlotIds[0];
}
function resolvePreferredPriceLabelScaleSlotId(snapshot, requestedScaleSlotId) {
    if (requestedScaleSlotId && snapshot.scaleSlotStates[requestedScaleSlotId]?.side === "right") {
        return requestedScaleSlotId;
    }
    const visibleScaleSlotIds = resolveVisibleScaleSlotIds(snapshot);
    if (visibleScaleSlotIds.length === 0) {
        return undefined;
    }
    return visibleScaleSlotIds.find((scaleSlotId) => snapshot.scaleSlotStates[scaleSlotId]?.side === "right")
        ?? (requestedScaleSlotId && snapshot.scaleSlotStates[requestedScaleSlotId] ? requestedScaleSlotId : undefined)
        ?? visibleScaleSlotIds[0];
}
/** Read a datum timestamp in ms (Date or epoch). Null when unparseable. */
function readDatumTimeMs(datum) {
    const raw = datum?.date;
    const ms = raw instanceof Date ? raw.getTime() : typeof raw === "number" ? raw : NaN;
    return Number.isFinite(ms) ? ms : null;
}
/** Linear scan for an exact timestamp match. Returns -1 when absent. */
function findDatumIndexByTimeMs(data, ms) {
    for (let index = 0; index < data.length; index += 1) {
        if (readDatumTimeMs(data[index]) === ms) {
            return index;
        }
    }
    return -1;
}
export class KernelEngine {
    /** Ad-enforcement utilities (included in DTS to ensure ad-tier logic is bundled) */
    static AD_TIER_UTILS = { FREE_AD_TIERS, requiresAds };
    options;
    listeners = new Map();
    paneLayout = new PaneLayoutEngine();
    paneSpecs = [];
    paneSnapshots = [];
    visibleRange = DEFAULT_VISIBLE_RANGE;
    theme = DEFAULT_THEME;
    locale = DEFAULT_LOCALE;
    dataSource = null;
    /** Type-erased enriched bars from app layer ΓÇö set via setEnrichedData() */
    enrichedData = [];
    container = null;
    surface = null;
    canvas = null;
    /**
     * Device-pixel ratio used for the canvas backing store. All drawing code
     * stays in CSS pixels — refreshSurface/refreshOverlay scale the backing
     * store (css × dpr) and install a matching setTransform, so 1px strokes
     * (grid, crosshair, wicks) stay crisp on HiDPI / Windows-scaled displays
     * instead of rendering blurry-bold from browser upscaling.
     */
    devicePixelRatio = 1;
    axisGridRenderer = new AxisGridRenderer();
    axisGridCache = new AxisGridRenderCache(this.axisGridRenderer);
    axisLabelFormatters;
    lineAreaRenderer = new LineAreaRenderer();
    barRenderer = new BarRenderer();
    candlestickRenderer = new CandlestickRenderer();
    priceLineRenderer = new PriceLineRenderer();
    priceLineOverlay = new PriceLineOverlay(this.priceLineRenderer);
    lastCandlestickRenderCache = new Map();
    candlestickLodWorkerLane;
    crosshairRenderer = new CrosshairRenderer();
    crosshairPos = null;
    /** P2 (E3): crosshair display mode (`cross` default) + OHLC magnet sensitivity (`off` default). */
    crosshairMode = "cross";
    crosshairMagnet = "off";
    interactionController = null;
    pendingRender = false;
    renderHandle = null;
    renderHandleKind = null;
    /**
     * BFCACHE-FIX: cleanup for the pageshow listener (Back-Forward Cache
     * restore). A scheduled rAF/timeout handle dies while the page is cached,
     * leaving pendingRender=true forever → every later scheduleRender
     * early-returns and the chart freezes with zero errors.
     */
    pageShowCleanup = null;
    lastPaneSnapshotsKey = null;
    overlayState = {
        crosshair: null,
        hoveredPaneId: null,
        hoveredDataIndex: null,
        xAxisTicks: [],
        measurement: null,
        drawingPriceLabels: null,
        drawingPrimitives: [],
    };
    overlayInputs = cloneOverlayInputs(DEFAULT_OVERLAY_INPUTS);
    measurementHoverPoint = null;
    measurementDraft = null;
    measurementCommitted = null;
    lastOverlayStateKey = null;
    disposed = false;
    patTier = "none";
    pat = "";
    serverPatTier = null;
    validatePatCallback;
    /** KF-12-T07: Dirty-reason mask — accumulates layer flags until flushed by RAF */
    pendingDirtyMask = DirtyFlag.NONE;
    /** KF-12-T08: Last mask consumed by flushScheduledRender (for test introspection) */
    lastFlushedMask = DirtyFlag.NONE;
    /** KF-12-T08: Overlay canvas for crosshair/hover (sits above main canvas, pointer-events: none) */
    /** Candle jump fix: previous LOD bucket size for hysteresis at threshold boundaries */
    previousLodBucketSize = 1;
    overlayCanvas = null;
    /** Y-range derived cache keyed by field name */
    derivedCaches = new Map();
    customSlotDomains = new Map();
    /** 5b-ii: per-scale-slot price projection mode (absent = linear). Indicator slots must stay linear. */
    /** Slots already warned about log fallback (Q2: warn once per activation). */
    priceScaleController = new PriceScaleController();
    activeDragSlot = null;
    isDraggingYAxis = false;
    activePanPaneId = null;
    /** Fields for which y-range caches are maintained */
    static CACHED_FIELDS = ["close", "high", "low", "volume"];
    // ── KF-12-T06: Camera state ──────────────────────────────────────────────
    /** Pixels per bar (zoom level). Derived from visibleRange + canvas width. */
    barSpacePx = 8;
    /** Bars from the right edge of the visible window to the last data bar. 0 = live edge. */
    rightOffsetBars = 0;
    /** Carries sub-bar drag pixels so deep zoom pan does not get stuck on per-event rounding. */
    panRemainderPx = 0;
    cameraEnvelopeMode = "shared";
    /**
     * When true the camera follows the latest bar so live-appends auto-scroll.
     * When false the camera is locked to the current rightOffsetBars position.
     */
    followLatest = true;
    /**
     * Absolute visibleRange.to captured after the last user gesture (zoom/pan).
     * Used in syncCameraAfterDataChange to preserve the user's intended viewport
     * position instead of recomputing from rightOffsetBars, which can snap to the
     * data edge when rightOffsetBars ≈ 0 after a gesture.
     */
    gestureAnchorTo = null;
    /**
     * When true, syncCameraAfterDataChange (section 4) preserves the current
     * visibleRange instead of recomputing from rightOffsetBars on data append.
     * Set by zoomToRange(), cleared by user gestures and explicit setCameraState.
     * Prevents visibleRange oscillation in multi-chart sync.
     */
    _zoomToRangeActive = false;
    /**
     * AUTH-01: a non-empty PAT is unverified until a host callback or a signed
     * license says otherwise. `getEffectivePatTier()` is fail-closed meanwhile.
     */
    patVerified = false;
    // ─────────────────────────────────────────────────────────────────────────
    constructor(options = {}) {
        this.options = {
            useWebGL: options.useWebGL ?? false,
            patTier: options.patTier ?? "none",
            pat: options.pat ?? "",
            priceLines: options.priceLines,
        };
        this.patTier = this.options.patTier;
        this.pat = options.pat ?? "";
        this.validatePatCallback = options.validatePat;
        if (this.pat) {
            // AUTH-01: a construction-time PAT is unverified until proven.
            this.patVerified = false;
            this._validatePat(this.pat);
        }
        this.candlestickLodWorkerLane = new CandlestickLodWorkerLane(() => this.disposed, (lodData) => this.resolveCandlestickLodDomain(lodData), () => this.scheduleRender(DirtyFlag.PLOT));
    }
    mount(container) {
        if (this.container === container && this.surface !== null) {
            return;
        }
        if (this.surface !== null) {
            this.dispose("remount");
        }
        const surface = container.ownerDocument.createElement("div");
        surface.setAttribute("data-vn-kline-engine", "surface");
        surface.setAttribute("data-engine-upstream", UPSTREAM_BASELINE.repo);
        surface.style.position = "relative";
        surface.style.width = "100%";
        surface.style.height = "100%";
        surface.style.overflow = "hidden";
        const canvas = container.ownerDocument.createElement("canvas");
        canvas.setAttribute("data-vn-kline-engine", "canvas");
        canvas.style.display = "block";
        canvas.style.width = "100%";
        canvas.style.height = "100%";
        // KF-12-T08: Overlay canvas sits above main canvas; DOM overlays read overlayState separately.
        const overlayCanvas = container.ownerDocument.createElement("canvas");
        overlayCanvas.setAttribute("data-vn-kline-engine", "overlay");
        overlayCanvas.style.position = "absolute";
        overlayCanvas.style.top = "0";
        overlayCanvas.style.left = "0";
        overlayCanvas.style.width = "100%";
        overlayCanvas.style.height = "100%";
        overlayCanvas.style.pointerEvents = "none";
        surface.append(canvas);
        surface.append(overlayCanvas);
        container.append(surface);
        this.container = container;
        this.surface = surface;
        this.canvas = canvas;
        this.overlayCanvas = overlayCanvas;
        this.disposed = false;
        // BFCACHE-FIX: a render scheduled right before the page enters the
        // Back-Forward Cache never flushes (rAF frozen, handle dead) while
        // pendingRender stays true → the render loop is dead after restore.
        // pageshow with persisted=true is the exact restore signal.
        const view = container.ownerDocument.defaultView ?? (typeof window !== "undefined" ? window : undefined);
        if (view) {
            const onPageShow = (e) => {
                if (!e.persisted || this.disposed)
                    return;
                this.pendingRender = false;
                this.renderHandle = null;
                this.renderHandleKind = null;
                this.scheduleRender(DirtyFlag.FULL);
            };
            view.addEventListener("pageshow", onPageShow);
            this.pageShowCleanup = () => view.removeEventListener("pageshow", onPageShow);
        }
        this.refreshSurface();
        this.interactionController = new InteractionController(canvas, {
            onZoom: (delta, pivotX, pivotY) => this.handleZoom(delta, pivotX, pivotY),
            onPan: (deltaX, deltaY, context) => {
                if (this.overlayInputs.measurementEnabled || this.isDraggingYAxis) {
                    return;
                }
                this.handlePan(deltaX, deltaY, context);
            },
            onCrosshairMove: (x, y) => {
                this.crosshairPos = { x, y };
                this.emitOverlayStateIfChanged();
                this.scheduleRender(DirtyFlag.OVERLAY);
            },
            onCrosshairLeave: () => {
                this.crosshairPos = null;
                this.emitOverlayStateIfChanged();
                this.scheduleRender(DirtyFlag.OVERLAY);
            },
            onPointerDown: (context) => {
                const slot = this.findScaleSlotAt(context.localX, context.localY);
                if (slot) {
                    const snapshot = this.paneSnapshots.find(s => s.paneId === slot.paneId);
                    const currentDomain = snapshot?.scaleSlotStates[slot.scaleSlotId]?.domain;
                    if (currentDomain) {
                        this.activeDragSlot = {
                            paneId: slot.paneId,
                            scaleSlotId: slot.scaleSlotId,
                            startY: context.clientY,
                            startDomain: [...currentDomain],
                        };
                        this.isDraggingYAxis = true;
                    }
                }
                else {
                    const snapshot = this.resolveMeasurementSnapshot(context.localX, context.localY);
                    if (snapshot) {
                        this.activePanPaneId = snapshot.paneId;
                    }
                }
                this.handleMeasurementPointerDown(context);
                this.emitInteraction("pointerdown", context);
            },
            onPointerMove: (context) => {
                if (this.isDraggingYAxis && this.activeDragSlot) {
                    const snapshot = this.paneSnapshots.find(s => s.paneId === this.activeDragSlot.paneId);
                    const height = snapshot ? snapshot.plotBounds.height : 100;
                    const diffY = context.clientY - this.activeDragSlot.startY;
                    // P2 (E1): Shift+drag = fine zoom at 0.5x rate (shared core with zoomYAxis).
                    const newDomain = zoomDomainByPixels(this.activeDragSlot.startDomain, diffY, height, context.shiftKey === true);
                    this.customSlotDomains.set(this.activeDragSlot.scaleSlotId, newDomain);
                    this.scheduleRender(DirtyFlag.PLOT | DirtyFlag.AXIS | DirtyFlag.OVERLAY);
                    this.emitOverlayStateIfChanged();
                }
                this.handleMeasurementPointerMove(context);
                this.emitInteraction("pointermove", context);
            },
            onPointerUp: (context) => {
                this.isDraggingYAxis = false;
                this.activeDragSlot = null;
                this.activePanPaneId = null;
                this.emitInteraction("pointerup", context);
            },
            onPointerCancel: (context) => {
                this.isDraggingYAxis = false;
                this.activeDragSlot = null;
                this.activePanPaneId = null;
                this.handleMeasurementPointerCancel();
                this.emitInteraction("pointercancel", context);
            },
            onPointerLeave: (context) => {
                this.isDraggingYAxis = false;
                this.activeDragSlot = null;
                this.activePanPaneId = null;
                this.handleMeasurementPointerLeave();
                this.emitInteraction("pointerleave", context);
            },
            onClick: (context) => {
                this.handleMeasurementClick();
                this.emitInteraction("click", context);
            },
            onDoubleClick: (context) => {
                const slot = this.findScaleSlotAt(context.localX, context.localY);
                if (slot) {
                    // P2: shared with the public resetYAxisZoom API — single source.
                    this.resetYAxisZoom(slot.scaleSlotId);
                    return;
                }
                this.emitInteraction("doubleclick", context);
            },
            onContextMenu: (context) => this.emitInteraction("contextmenu", context),
            onDragStart: (context) => {
                this.panRemainderPx = 0;
                this.emitInteraction("dragstart", context);
            },
            onDragMove: (context) => this.emitInteraction("dragmove", context),
            onDragEnd: (context) => this.emitInteraction("dragend", context),
        });
        this.emit("mount", { container });
    }
    setPaneSpecs(panes) {
        this.paneSpecs = [...panes];
        this.scheduleRender(DirtyFlag.FULL);
    }
    setDataSource(dataSource) {
        this.dataSource = dataSource;
        this.scheduleRender(DirtyFlag.FULL);
        this.emit("dataSource", { dataSource });
    }
    /**
     * Supply pre-computed indicator data from the app layer.
     * Data is type-erased (Record<string, unknown>[]) to avoid cross-layer coupling.
     * KernelEngine uses INDICATOR_ACCESSORS to extract scalar values per series type.
     */
    setEnrichedData(data) {
        if (this.enrichedData === data) {
            this.scheduleRender(DirtyFlag.PLOT | DirtyFlag.AXIS | DirtyFlag.OVERLAY);
            return;
        }
        const prevLength = this.enrichedData.length;
        const appendedCount = data.length > prevLength ? data.length - prevLength : 0;
        const prevData = this.enrichedData;
        this.candlestickLodWorkerLane.abortAll();
        this.enrichedData = data;
        this.rebuildDerivedCaches(prevLength, 0, appendedCount);
        this.syncCameraAfterDataChange(prevLength, 0, appendedCount, prevData);
        this.syncSnapshotsAndEmit();
        this.scheduleRender(DirtyFlag.PLOT | DirtyFlag.AXIS | DirtyFlag.OVERLAY);
        // Emit updated visibleRange so React state stays in sync after data changes
        // (mirrors legacy: D3 chart always re-derives and emits range on every data update).
        this.emit("visibleRange", { range: this.getVisibleRange(), origin: "programmatic" });
    }
    /**
     * Push an immutable history snapshot from the widget chunk-store.
     * The engine does NOT need to know about the adapter, fetch strategy, or
     * pagination — it only receives immutable snapshots.
     *
     * `patch` may be used in the future to invalidate derived caches
     * incrementally.  For now the engine forwards the bars to enrichedData.
     *
     * NOTE: The snapshot's `bars` are type-erased at the engine boundary;
     * the engine casts them to `Record<string, unknown>[]` for `setEnrichedData`.
     */
    setHistorySnapshot(snapshot, patch) {
        const prevLength = this.enrichedData.length;
        const prependedCount = patch?.kind === "prepend" ? (patch.prependedCount ?? 0) : 0;
        const appendedCount = patch?.kind === "append" ? (patch.appendedCount ?? 0) : 0;
        const prevData = this.enrichedData;
        this.candlestickLodWorkerLane.abortAll();
        this.enrichedData = snapshot.bars;
        // Z4-02/Z4-03: dataset shape changed (symbol/TF switch) — drop the stale
        // LOD hysteresis state so the first frame renders unaggregated (no flash).
        if (prevLength > 0 && Math.abs(this.enrichedData.length - prevLength) > prevLength * 0.5) {
            this.previousLodBucketSize = 1;
        }
        this.rebuildDerivedCaches(prevLength, prependedCount, appendedCount);
        this.syncCameraAfterDataChange(prevLength, prependedCount, appendedCount, prevData);
        this.syncSnapshotsAndEmit();
        this.scheduleRender(DirtyFlag.PLOT | DirtyFlag.AXIS | DirtyFlag.OVERLAY);
        this.emit("visibleRange", { range: this.getVisibleRange(), origin: "programmatic" });
    }
    /**
     * KF-12-T05: Query y-range for a visible index window using the derived cache.
     * O(k) where k = overlapping bucket count — avoids O(n) full scan on pan/zoom.
     *
     * Returns null if no cache exists for the field or no bars in range.
     */
    getYRangeFromCache(fieldKey, fromIndex, toIndex) {
        const cache = this.derivedCaches.get(fieldKey);
        if (!cache)
            return null;
        return queryYRange(cache, fromIndex, toIndex);
    }
    // ── KF-12-T05: Derived cache management ─────────────────────────────────
    rebuildDerivedCaches(prevLength, prependedCount = 0, appendedCount = 0) {
        const data = this.enrichedData;
        for (const field of KernelEngine.CACHED_FIELDS) {
            const existing = this.derivedCaches.get(field);
            if (!existing) {
                this.derivedCaches.set(field, createDerivedCache(data, field));
            }
            else if (data.length === 0) {
                this.derivedCaches.set(field, createDerivedCache([], field));
            }
            else if (prependedCount > 0 && data.length > prevLength) {
                this.derivedCaches.set(field, prependHeadCache(data, prependedCount, existing));
            }
            else if (data.length > prevLength) {
                this.derivedCaches.set(field, appendTailCache(data, prevLength, existing));
            }
            else {
                this.derivedCaches.set(field, replaceAllCache(data, existing));
            }
        }
    }
    // ── KF-12-T06: Camera helpers ────────────────────────────────────────────
    /**
     * When followLatest=true, auto-advance the visible window to show the newest bars.
     * When followLatest=false, preserve the viewport's right-edge offset relative to live data.
     * If the preserved range falls completely outside the loaded data, re-anchor it to the nearest edge.
     */
    /**
     * P2-SCROLLBAR shrink-guard core. Called only for a proven net shrink with
     * readable boundary timestamps (see syncCameraAfterDataChange).
     * Pure head-trim (tail intact + lengths reconcile) shifts the range to
     * preserve content; any REMAINING new beyond-edge blank is pulled back.
     * Pre-existing overshoot (wall-clock gap, pan rubber-band) is preserved.
     */
    clampShrinkOvershoot(prevLength, dataLen, prevData, newHeadMs, newTailMs, prevTailMs) {
        const overshootBefore = Math.max(0, this.visibleRange.to - prevLength);
        let headShift = 0;
        if (newTailMs === prevTailMs) {
            const found = findDatumIndexByTimeMs(prevData, newHeadMs);
            if (found > 0 && prevLength - found === dataLen) {
                headShift = found;
            }
        }
        if (headShift > 0) {
            this.visibleRange = {
                from: this.visibleRange.from - headShift,
                to: this.visibleRange.to - headShift,
            };
            if (this.gestureAnchorTo !== null) {
                this.gestureAnchorTo = Math.max(0, this.gestureAnchorTo - headShift);
            }
        }
        const overshootAfter = Math.max(0, this.visibleRange.to - dataLen);
        if (overshootAfter > overshootBefore) {
            const pullBack = overshootAfter - overshootBefore;
            this.visibleRange = {
                from: this.visibleRange.from - pullBack,
                to: this.visibleRange.to - pullBack,
            };
            if (this.gestureAnchorTo !== null) {
                this.gestureAnchorTo = Math.max(0, this.gestureAnchorTo - pullBack);
            }
        }
        // Keep the legal left envelope (pan convention: from >= -span).
        const shrunkSpan = Math.max(1, this.visibleRange.to - this.visibleRange.from);
        if (this.visibleRange.from < -shrunkSpan) {
            const lift = -shrunkSpan - this.visibleRange.from;
            this.visibleRange = { from: -shrunkSpan, to: this.visibleRange.to + lift };
        }
    }
    syncCameraAfterDataChange(prevLength, prependedCount = 0, appendedCount = 0, prevData) {
        const dataLen = this.enrichedData.length;
        if (dataLen === 0)
            return;
        // P2-SCROLLBAR (shrink guard): a net data shrink (retention trim) must not
        // strand the viewport in blank space, and must never GROW the beyond-edge
        // overshoot. Invariant: range in-bounds (modulo pre-existing overshoot).
        // Content-preserving head-shift first, then cap only NEW blank.
        // Date-gated: without readable boundary timestamps the shrink geometry is
        // unprovable (could be a head-trim where the range is fully valid), so
        // legacy preserve-behavior applies (KF12-T06-05).
        if (prevLength !== undefined && prevLength > dataLen && prevData && prevData.length === prevLength) {
            const prevHeadMs = readDatumTimeMs(prevData[0]);
            const prevTailMs = readDatumTimeMs(prevData[prevLength - 1]);
            const newHeadMs = readDatumTimeMs(this.enrichedData[0]);
            const newTailMs = readDatumTimeMs(this.enrichedData[dataLen - 1]);
            const dated = prevHeadMs !== null && prevTailMs !== null && newHeadMs !== null && newTailMs !== null;
            if (dated) {
                this.clampShrinkOvershoot(prevLength, dataLen, prevData, newHeadMs, newTailMs, prevTailMs);
            }
        }
        const rangeBefore = { ...this.visibleRange };
        let reason = '';
        // 0. Prepend shift: when bars were prepended before the existing data,
        // shift the viewport forward by prependedCount so the same candles
        // remain visible at their new indices.
        if (prependedCount > 0) {
            // When zoomToRange was called explicitly, preserve the programmatic intent
            // (e.g. "show from index 0") instead of shifting the range forward. This
            // matches the existing Section 4a behavior for append and prevents
            // visibleRange oscillation in multi-chart sync when data grows on the left.
            if (this._zoomToRangeActive) {
                this.syncCameraStateFromVisibleRange();
                return;
            }
            const { from, to } = this.visibleRange;
            const newFrom = from + prependedCount;
            const newTo = to + prependedCount;
            const span = newTo - newFrom;
            let clampedFrom = newFrom;
            let clampedTo = newTo;
            if (clampedFrom < -span) {
                clampedFrom = -span;
                clampedTo = clampedFrom + span;
            }
            else if (clampedFrom >= dataLen && dataLen > 0) {
                // Shifted range starts at or past data edge (data trimmed after prepend) → snap to tail
                clampedTo = dataLen;
                clampedFrom = Math.max(0, clampedTo - span);
            }
            else if (clampedTo > dataLen + span) {
                clampedTo = dataLen + span;
                clampedFrom = clampedTo - span;
            }
            this.visibleRange = { from: clampedFrom, to: clampedTo };
            reason = 'PREPEND_SHIFT';
            // Snap gesture anchor to the new visibleRange.to (clampedTo) instead of
            // blindly adding prependedCount. gestureAnchorTo may be stale (ahead of
            // visibleRange.to from a prior scroll), and += would preserve that stale
            // delta, causing GESTURE_ANCHOR to overshoot and clamp to the data tail.
            if (this.gestureAnchorTo !== null) {
                const prevAnchor = this.gestureAnchorTo;
                this.gestureAnchorTo = clampedTo;
            }
            this.syncCameraStateFromVisibleRange();
            return;
        }
        // 0b. Append shift: when bars were appended after the existing data,
        // shift the gesture anchor forward by appendedCount so the newly
        // appended candles remain visible at the right edge.
        if (appendedCount > 0 && prevLength !== undefined && this.gestureAnchorTo !== null) {
            if (this.gestureAnchorTo > prevLength) {
                // Anchor is in extended envelope (user panned/zoomed past data edge).
                // Maintain the same extended offset past the data edge.
                this.gestureAnchorTo += appendedCount;
            }
            else {
                // Anchor is at or within the data bounds.
                // Only shift if anchor was at or very near the data edge (within 2 bars).
                // This covers the common "pan right + backfill" case without disturbing
                // anchors in the middle of the dataset.
                const edgeDistance = prevLength - this.gestureAnchorTo;
                if (edgeDistance <= 2) {
                    this.gestureAnchorTo += appendedCount;
                }
            }
        }
        const currentSpan = Math.max(1, this.visibleRange.to - this.visibleRange.from);
        // 1. If followLatest is active AND no gesture anchor exists, auto-scroll.
        // Gesture anchor (set by handleZoom/handlePan) takes priority over followLatest;
        // it persists until the next explicit gesture or programmatic override.
        // _zoomToRangeActive: programmatic range set (e.g. cross-chart sync) should
        // NOT auto-scroll to right edge — the synced range must be preserved until
        // the user interacts. Without this guard, non-active charts snap "now" to
        // the right edge on every data update, overriding the synced pan position.
        if (this.followLatest && this.gestureAnchorTo === null && !this._zoomToRangeActive) {
            const minSpan = this.resolveMinSpan(dataLen);
            const span = Math.max(minSpan, currentSpan);
            const newTo = dataLen;
            const newFrom = Math.max(0, newTo - span);
            this.visibleRange = { from: newFrom, to: newTo };
            this.syncCameraStateFromVisibleRange();
            return;
        }
        // 2. History mode (followLatest === false)
        // Snap-to-fit only when data was actually removed (size decreased).
        // Does NOT trigger when user zooms out making span > dataLen — that
        // would pin first/last candles to viewport edges and block zoom-out.
        if (dataLen < currentSpan && prevLength !== undefined && dataLen < prevLength) {
            this.visibleRange = { from: 0, to: dataLen };
            this.syncCameraStateFromVisibleRange();
            return;
        }
        const overlapSpan = Math.max(1, Math.min(5, dataLen));
        // 3. Gesture anchor: preserve absolute visibleRange.to from last user gesture.
        // The anchor persists until the NEXT gesture (handleZoom/handlePan) or
        // programmatic call (setCameraState/zoomToRange/scrollToDataIndex) clears it.
        // Without this, rightOffsetBars ≈ 0 makes targetTo = dataLen in the
        // rightOffset-based branch, snapping the viewport to the data edge on
        // every data append after a gesture (Bug 1 & Bug 3 fix).
        if (this.gestureAnchorTo !== null) {
            const newTo = this.gestureAnchorTo;
            const newFrom = newTo - currentSpan;
            // Clamp when anchor pushes range beyond data bounds (prevents candles
            // bunching at the left edge after prepend + data-trim).
            // Distinguish "user zoomed out past data" (extended envelope) from
            // "data shrank making anchor stale": if anchor was within prevLength
            // bounds when set, data must have shrunk → clamp. If anchor already
            // exceeded prevLength (user intentionally zoomed out), preserve.
            const anchorExceededPrev = prevLength !== undefined && this.gestureAnchorTo > prevLength;
            let clampedRange;
            if (newTo <= 0) {
                const anchorSpan = Math.max(overlapSpan, Math.min(currentSpan, dataLen));
                clampedRange = { from: 0, to: anchorSpan };
            }
            else if (newFrom > dataLen && dataLen > 0) {
                // Entire range after data edge → clamp
                const anchorSpan = Math.max(overlapSpan, Math.min(currentSpan, dataLen));
                clampedRange = { from: Math.max(0, dataLen - anchorSpan), to: dataLen };
            }
            else if (newTo > dataLen && dataLen > 0 && !anchorExceededPrev) {
                // Anchor was within data bounds when set, but data shrank → clamp
                const anchorSpan = Math.max(overlapSpan, Math.min(currentSpan, dataLen));
                clampedRange = { from: Math.max(0, dataLen - anchorSpan), to: dataLen };
            }
            else {
                clampedRange = { from: newFrom, to: newTo };
            }
            this.visibleRange = clampedRange;
            // Keep gestureAnchorTo — it persists until the next explicit gesture
            // or programmatic override, preventing followLatest from re-engaging
            // via syncCameraStateFromVisibleRange below.
            this.syncCameraStateFromVisibleRange();
            return;
        }
        // 4a. Zoom-to-range anchor: when zoomToRange was called explicitly, preserve
        // the range even when data grows/shrinks, preventing visibleRange oscillation
        // in multi-chart sync. The flag is cleared on user gesture (handleZoom/handlePan)
        // or explicit setCameraState, restoring the old behavior.
        if (this._zoomToRangeActive) {
            this.syncCameraStateFromVisibleRange();
            return;
        }
        const targetTo = dataLen - this.rightOffsetBars;
        const targetFrom = targetTo - currentSpan;
        if (targetTo <= 0) {
            const anchorSpan = Math.max(overlapSpan, Math.min(currentSpan, dataLen));
            this.visibleRange = { from: 0, to: anchorSpan };
        }
        else if (targetFrom >= dataLen) {
            const anchorSpan = Math.max(overlapSpan, Math.min(currentSpan, dataLen));
            this.visibleRange = { from: Math.max(0, dataLen - anchorSpan), to: dataLen };
        }
        else {
            this.visibleRange = { from: targetFrom, to: targetTo };
        }
        this.syncCameraStateFromVisibleRange();
    }
    resolveCameraEnvelopeMode() {
        const dataLen = this.enrichedData.length;
        if (dataLen > 0 && (this.visibleRange.from < 0 || this.visibleRange.to > dataLen)) {
            return "extended";
        }
        if (this.barSpacePx < SHARED_ENVELOPE_MIN_BAR_SPACE_PX || this.barSpacePx > SHARED_ENVELOPE_MAX_BAR_SPACE_PX) {
            return "extended";
        }
        return "shared";
    }
    normalizeVisibleRange(range) {
        const from = Number.isFinite(range.from)
            ? Math.max(-CAMERA_RANGE_MAX_ABS_INDEX, Math.min(CAMERA_RANGE_MAX_ABS_INDEX, range.from))
            : 0;
        const to = Number.isFinite(range.to)
            ? Math.max(-CAMERA_RANGE_MAX_ABS_INDEX, Math.min(CAMERA_RANGE_MAX_ABS_INDEX, range.to))
            : from;
        let result = from <= to ? { from, to } : { from: to, to: from };
        // Enforce minSpan: prevent zoom-in beyond what SAFE_MAX_BAR_WIDTH allows
        const dataLen = this.enrichedData.length;
        if (dataLen > 0) {
            const minSpan = this.resolveMinSpan(dataLen);
            const span = result.to - result.from;
            if (span < minSpan) {
                result = { from: result.from, to: result.from + minSpan };
            }
        }
        if (range.from !== result.from || range.to !== result.to) {
        }
        return result;
    }
    resolveXAxisViewport() {
        const primaryPane = this.paneSnapshots[0];
        if (primaryPane?.plotBounds && Number.isFinite(primaryPane.plotBounds.width) && primaryPane.plotBounds.width > 0) {
            return {
                left: primaryPane.plotBounds.left,
                width: primaryPane.plotBounds.width,
            };
        }
        return {
            left: 0,
            // canvas.width is the DPR backing store — divide back to CSS px.
            // (Keeps the long-standing contract that canvas.width tracks layout width.)
            width: Math.max(1, (this.canvas ? this.canvas.width / this.devicePixelRatio : 0) || DEFAULT_WIDTH),
        };
    }
    /**
     * Read the display scale for DPR-aware canvas backing. Clamped to [1, 3]:
     * above 3x the backing store cost outweighs visible crispness gains.
     * Defensive (try/catch + fallbacks) — must never throw on exotic hosts
     * (SSR, workers, mocked DOM in tests).
     */
    resolveDevicePixelRatio() {
        try {
            const raw = this.container?.ownerDocument?.defaultView?.devicePixelRatio;
            if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) {
                return Math.min(3, Math.max(1, raw));
            }
        }
        catch {
            // fall through to 1
        }
        return 1;
    }
    syncCameraStateFromVisibleRange() {
        const beforeRange = { ...this.visibleRange };
        this.visibleRange = this.normalizeVisibleRange(this.visibleRange);
        const span = Math.max(1, this.visibleRange.to - this.visibleRange.from);
        const xViewport = this.resolveXAxisViewport();
        this.barSpacePx = xViewport.width / span;
        const dataLen = this.enrichedData.length;
        const rightOffset = dataLen - this.visibleRange.to;
        // Do NOT auto-set followLatest based on position.
        // followLatest is only set by:
        // 1. setCameraState({ followLatest: true }) — explicit user request
        // 2. syncCameraAfterDataChange when new data arrives AND followLatest was already true
        // Position-based auto-snap was pinning viewport to right edge after zoom clamp.
        this.rightOffsetBars = rightOffset;
        // this.followLatest = Math.abs(rightOffset) < 1e-10;  // REMOVED
        this.cameraEnvelopeMode = this.resolveCameraEnvelopeMode();
    }
    /**
     * KF-12-T06: Explicitly set camera parameters.
     * Allows host (EngineHost / widget) to set followLatest mode.
     */
    setCameraState(state) {
        this._zoomToRangeActive = false;
        if (state.followLatest !== undefined) {
            this.followLatest = state.followLatest;
            if (state.followLatest) {
                this.gestureAnchorTo = null;
            }
        }
        if (state.rightOffsetBars !== undefined) {
            this.rightOffsetBars = state.rightOffsetBars;
            // Clear gesture anchor when rightOffsetBars is explicitly set, so
            // syncCameraAfterDataChange will use the rightOffsetBars logic instead
            // of being blocked by a stale gestureAnchorTo from a prior gesture.
            this.gestureAnchorTo = null;
        }
        // If following latest, scroll to edge
        if (this.followLatest) {
            this.syncCameraAfterDataChange();
        }
        else if (state.rightOffsetBars !== undefined) {
            const dataLen = this.enrichedData.length;
            const effectiveDataLen = dataLen || 1000;
            const minSpan = this.resolveMinSpan(effectiveDataLen);
            const currentSpan = Math.max(minSpan, this.visibleRange.to - this.visibleRange.from);
            const targetTo = effectiveDataLen - this.rightOffsetBars;
            const targetFrom = targetTo - currentSpan;
            // Respect the soft limits
            const maxSpan = MAX_VISIBLE_BARS;
            const clampedSpan = Math.max(minSpan, Math.min(maxSpan, currentSpan));
            const clampedFrom = Math.max(-clampedSpan, Math.min(effectiveDataLen, targetFrom));
            this.visibleRange = {
                from: clampedFrom,
                to: clampedFrom + clampedSpan,
            };
            this.syncCameraStateFromVisibleRange();
        }
        this.emitOverlayStateIfChanged();
        this.scheduleRender(DirtyFlag.PLOT | DirtyFlag.AXIS | DirtyFlag.OVERLAY);
    }
    setOverlayInputs(inputs) {
        const wasMeasurementEnabled = this.overlayInputs.measurementEnabled;
        this.overlayInputs = cloneOverlayInputs({ ...this.overlayInputs, ...inputs });
        if (wasMeasurementEnabled && !this.overlayInputs.measurementEnabled) {
            this.resetMeasurementState();
        }
        this.emitOverlayStateIfChanged();
        this.scheduleRender(DirtyFlag.OVERLAY);
    }
    setCrosshairPos(pos) {
        this.crosshairPos = pos;
        this.emitOverlayStateIfChanged();
        this.scheduleRender(DirtyFlag.OVERLAY);
    }
    setAxisLabelFormatters(formatters) {
        this.axisLabelFormatters = formatters;
        this.scheduleRender(DirtyFlag.AXIS);
    }
    dataIndexToPixelX(paneId, index) {
        const snapshot = this.paneSnapshots.find((s) => s.paneId === paneId);
        if (!snapshot)
            return null;
        return resolvePanePixelAtDataIndex(snapshot, this.visibleRange, index, this.enrichedData.length);
    }
    priceToPixelY(paneId, scaleSlotId, price) {
        const snapshot = this.paneSnapshots.find((s) => s.paneId === paneId);
        if (!snapshot)
            return null;
        return resolvePanePixelAtValue(snapshot, scaleSlotId, price);
    }
    /**
     * KF-12-T06: Read-only snapshot of current camera state.
     */
    getCameraState() {
        return {
            barSpacePx: this.barSpacePx,
            rightOffsetBars: this.rightOffsetBars,
            followLatest: this.followLatest,
            envelopeMode: this.cameraEnvelopeMode,
        };
    }
    findScaleSlotAt(localX, localY) {
        for (const snapshot of this.paneSnapshots) {
            for (const [slotId, bounds] of Object.entries(snapshot.scaleSlotBounds)) {
                const right = bounds.left + bounds.width;
                const bottom = bounds.top + bounds.height;
                if (localX >= bounds.left && localX <= right && localY >= bounds.top && localY <= bottom) {
                    const state = snapshot.scaleSlotStates[slotId];
                    if (state) {
                        return {
                            paneId: snapshot.paneId,
                            scaleSlotId: slotId,
                            side: state.side,
                            bounds,
                        };
                    }
                }
            }
        }
        return null;
    }
    handleZoom(delta, pivotX, pivotY) {
        if (pivotY !== undefined) {
            const slot = this.findScaleSlotAt(pivotX, pivotY);
            if (slot) {
                const paneSpec = this.paneSpecs.find((p) => p.id === slot.paneId);
                if (paneSpec) {
                    const slotDomains = this.computePaneScaleSlotDomains(paneSpec);
                    const currentDomain = this.customSlotDomains.get(slot.scaleSlotId) || slotDomains.get(slot.scaleSlotId);
                    if (currentDomain) {
                        const zoomMultiplier = 1.1;
                        const zoomDirection = delta > 0 ? -1 : 1; // delta > 0 means scroll up (zoom in)
                        const c = zoomDirection > 0 ? zoomMultiplier : 1 / zoomMultiplier;
                        const [min, max] = currentDomain;
                        const center = (min + max) / 2;
                        const newDomain = [
                            center + (min - center) * c,
                            center + (max - center) * c
                        ];
                        this.customSlotDomains.set(slot.scaleSlotId, newDomain);
                        this.scheduleRender(DirtyFlag.PLOT | DirtyFlag.AXIS | DirtyFlag.OVERLAY);
                        this.emitOverlayStateIfChanged();
                    }
                }
                return;
            }
        }
        const dataLen = this.enrichedData.length || 1000;
        const minSpan = this.resolveMinSpan(dataLen);
        const maxZoomSpan = MAX_VISIBLE_BARS;
        const { from, to } = this.visibleRange;
        const oldSpan = to - from;
        // Limit checks: return early to avoid redundant calculations, snapping, or re-renders
        const limitSpan = maxZoomSpan;
        if (delta < 0 && oldSpan >= limitSpan) {
            return;
        }
        if (delta > 0 && oldSpan <= minSpan) {
            return;
        }
        // STAGE 1: cap effective zoom to 1 notch per event
        const rawAbsDelta = Math.abs(delta);
        const step = Math.sign(delta) * Math.min(rawAbsDelta, 1.0);
        // STAGE 2: symmetric exponential zoom (Z1-02, D-Z04, SSOT ZOOM_K = ln(1.06)).
        // SIGN CONVENTION — LOCKED (Z1-03b, v2): delta > 0 (scroll up) shrinks the
        // span = zoom in; delta < 0 (scroll down) grows it = zoom out. Every caller
        // MUST send delta = -deltaY * modeFactor / 100 (see InteractionController).
        // Inverting the sign here (or in any coalescer) flips zoom direction 100%.
        const newSpanRaw = oldSpan * Math.exp(-ZOOM_K * step);
        const newSpanClipped = Math.max(minSpan, Math.min(maxZoomSpan, newSpanRaw));
        const xViewport = this.resolveXAxisViewport();
        let r = 0.5;
        if (typeof pivotX === "number" && Number.isFinite(pivotX)) {
            const localPivotX = pivotX - xViewport.left;
            r = Math.max(0, Math.min(1, localPivotX / xViewport.width));
        }
        // Cursor-anchored zoom (Z1-03, D-Z02 v2): the pivot is the mouse EXCEPT at
        // the live edge, where the range is ABSOLUTELY anchored (r = 1.0, so
        // newTo = to). The old 0.7 lerp is removed — it drifted `to` even when
        // pinned (r = 0.85 at cursor center) and snapped the pivot at from <= 5.
        const atRightEdge = dataLen - to < 2 || Math.abs(this.rightOffsetBars) < 2;
        if (atRightEdge) {
            r = RIGHT_EDGE_ANCHOR_R;
        }
        // STAGE 3: compute pivot-based range
        const pivotIndex = from + r * oldSpan;
        const newFromRaw = pivotIndex - r * newSpanClipped;
        const newToRaw = newFromRaw + newSpanClipped;
        let newFrom = newFromRaw;
        let newTo = newToRaw;
        let boundaryTriggered = '';
        // STAGE 4: soft boundary (Z3-03, D-Z06/D-Z16) — rubber-band damp instead of
        // hard snaps. `from < 0` / `to > dataLen` overscroll is allowed (extended
        // envelope); only degenerate off-data viewports snap back.
        // effectiveRightEdge: when user has panned right beyond dataLen (e.g. backfill
        // pending or intentional padding), preserve that position instead of snapping
        // back to dataLen which causes the "snap to origin" bug.
        const effectiveRightEdge = Math.max(to, dataLen);
        if (newToRaw <= 0) {
            boundaryTriggered = 'LEFT_OFF';
            newFrom = 0;
            newTo = newFrom + newSpanClipped;
        }
        else if (newFromRaw >= effectiveRightEdge) {
            boundaryTriggered = 'RIGHT_OFF';
            newTo = effectiveRightEdge;
            newFrom = newTo - newSpanClipped;
        }
        else {
            // Damp left overscroll toward the edge (keeps a negative `from`).
            if (newFromRaw < 0) {
                boundaryTriggered = 'LEFT_DAMP';
                newFrom = newFromRaw * 0.3;
                newTo = newFrom + newSpanClipped;
            }
            else {
                newFrom = newFromRaw;
                newTo = newToRaw;
            }
            // Damp right overshoot beyond the effective edge symmetrically.
            if (newTo > effectiveRightEdge) {
                boundaryTriggered = boundaryTriggered ? boundaryTriggered + '_RIGHT_DAMP' : 'RIGHT_DAMP';
                const overshoot = newTo - effectiveRightEdge;
                newTo = effectiveRightEdge + overshoot * 0.3;
                newFrom = newTo - newSpanClipped;
            }
            // NOTE (CENTER-snap removed): when the zoomed span exceeds the data
            // length the viewport used to teleport to {0, effectiveRightEdge} and
            // pin zoom-out forever. The extended envelope (from<0 / to>dataLen,
            // D-Z06) already represents this state — damp above gives rubber-band
            // resistance, no snap. Fully-off-data viewports are still caught by
            // LEFT_OFF / RIGHT_OFF above.
        }
        this.visibleRange = { from: newFrom, to: newTo };
        this.syncCameraStateFromVisibleRange();
        // Zoom is an explicit user gesture — disengage followLatest.
        this.followLatest = false;
        this._zoomToRangeActive = false;
        // Update gestureAnchorTo on every zoom (Z3-04): a boundary-damped zoom
        // still needs a valid anchor (clamped to data) so later appends track the
        // damped position instead of a stale one. Only a non-clamped zoom keeps
        // the right-edge exemption (user didn't intentionally sit at the edge).
        if (!boundaryTriggered) {
            const wasAtRightEdge = this.gestureAnchorTo !== null && this.gestureAnchorTo >= effectiveRightEdge - 1;
            if (!wasAtRightEdge) {
                this.gestureAnchorTo = this.visibleRange.to;
            }
        }
        else {
            this.gestureAnchorTo = Math.max(0, Math.min(dataLen, this.visibleRange.to));
        }
        this.syncSnapshotsAndEmit();
        this.emitOverlayStateIfChanged();
        this.scheduleRender(DirtyFlag.PLOT | DirtyFlag.AXIS | DirtyFlag.OVERLAY);
        this.emit("visibleRange", { range: this.getVisibleRange(), origin: "user" });
    }
    handlePan(deltaX, deltaY, context) {
        // P2 (E2): Alt+drag on the plot zooms time around the cursor instead of
        // panning. Sign follows the locked wheel convention (delta > 0 = zoom in):
        // drag left (deltaX < 0) zooms in, drag right zooms out.
        if (context?.altKey === true) {
            const pivotX = Number.isFinite(context.localX) ? context.localX : 0;
            this.handleZoom(-deltaX / 100, pivotX);
            return;
        }
        // CTRL-RUBBERBAND: Ctrl/Cmd+drag belongs to the widget rubber-band
        // selection (multi-select / zoom-to-selection gestures) — the chart
        // must stay frozen so the selection region keeps pixel accuracy.
        // (Mirrors the Alt precedent above; plain-drag pan is untouched.)
        if (context?.ctrlKey === true || context?.metaKey === true) {
            return;
        }
        const dataLen = this.enrichedData.length || 1000;
        const minSpan = this.resolveMinSpan(dataLen);
        const xViewport = this.resolveXAxisViewport();
        const { from, to } = this.visibleRange;
        const span = Math.max(minSpan, to - from);
        const pixelsPerBar = xViewport.width / Math.max(1, span);
        const totalDeltaPx = this.panRemainderPx + deltaX;
        const rawDeltaBars = totalDeltaPx / pixelsPerBar;
        const indexDelta = Math.round(rawDeltaBars);
        this.panRemainderPx = totalDeltaPx - indexDelta * pixelsPerBar;
        const newFrom = from - indexDelta;
        const clampedFrom = Math.max(-span, Math.min(dataLen, newFrom));
        this.visibleRange = { from: clampedFrom, to: clampedFrom + span };
        // Panning is an explicit user gesture — disengage followLatest.
        this.syncCameraStateFromVisibleRange();
        this.followLatest = false;
        this._zoomToRangeActive = false;
        this.gestureAnchorTo = this.visibleRange.to;
        // Vertical panning logic for manual scaling mode (Mode B)
        if (deltaY !== undefined && deltaY !== 0 && this.activePanPaneId) {
            const snapshot = this.paneSnapshots.find(s => s.paneId === this.activePanPaneId);
            if (snapshot) {
                let hasManualScale = false;
                for (const slotId of Object.keys(snapshot.scaleSlotStates)) {
                    if (this.customSlotDomains.has(slotId)) {
                        hasManualScale = true;
                        break;
                    }
                }
                if (hasManualScale) {
                    const height = snapshot.plotBounds.height;
                    if (height > 0) {
                        for (const [slotId, slotState] of Object.entries(snapshot.scaleSlotStates)) {
                            const currentDomain = this.customSlotDomains.get(slotId) || slotState.domain;
                            if (currentDomain) {
                                const [min, max] = currentDomain;
                                const range = max - min;
                                if (range > 0 && Number.isFinite(range)) {
                                    const priceShift = (deltaY / height) * range;
                                    const newDomain = [min + priceShift, max + priceShift];
                                    this.customSlotDomains.set(slotId, newDomain);
                                }
                            }
                        }
                    }
                }
            }
        }
        this.syncSnapshotsAndEmit();
        this.emitOverlayStateIfChanged();
        this.scheduleRender(DirtyFlag.PLOT | DirtyFlag.AXIS | DirtyFlag.OVERLAY);
        this.emit("visibleRange", { range: this.getVisibleRange(), origin: "user" });
    }
    setTheme(theme) {
        this.theme = theme;
        this.scheduleRender(DirtyFlag.FULL);
        this.emit("theme", { theme });
    }
    setLocale(locale) {
        this.locale = locale;
        this.scheduleRender(DirtyFlag.FULL);
    }
    /** 5b-ii: set price scale projection for one scale slot (default "price-right"). */
    setPriceScaleMode(mode, scaleSlotId = "price-right") {
        if (mode !== "linear" && mode !== "log" && mode !== "percent") {
            console.warn(`[KernelEngine] Ignoring unknown price scale mode: ${String(mode)}`);
            return;
        }
        if (!this.priceScaleController.setMode(mode, scaleSlotId)) {
            return;
        }
        this.scheduleRender(DirtyFlag.PLOT | DirtyFlag.AXIS | DirtyFlag.OVERLAY);
    }
    /** A3: invert the price axis for one scale slot (default "price-right"). */
    setPriceScaleInverted(inverted, scaleSlotId = "price-right") {
        if (!this.priceScaleController.setInverted(inverted, scaleSlotId)) {
            return;
        }
        this.scheduleRender(DirtyFlag.PLOT | DirtyFlag.AXIS | DirtyFlag.OVERLAY);
    }
    /** W0 Task 0.3: move the price axis gutter (default slot "price-right"). */
    setPriceAxisPosition(side, scaleSlotId = "price-right") {
        if (this.priceScaleController.setSide(side, scaleSlotId))
            this.scheduleRender(DirtyFlag.FULL);
    }
    /**
     * P2 (E3): crosshair display mode. `dot` hides the cross lines (beacon only);
     * `magnet` snaps Y to the hovered-bar OHLC per the magnet sensitivity.
     */
    setCrosshairMode(mode) {
        if (mode !== "cross" && mode !== "dot" && mode !== "magnet") {
            console.warn(`[KernelEngine] Ignoring unknown crosshair mode: ${String(mode)}`);
            return;
        }
        if (this.crosshairMode === mode) {
            return;
        }
        this.crosshairMode = mode;
        this.emitOverlayStateIfChanged();
        this.scheduleRender(DirtyFlag.OVERLAY);
    }
    /** P2 (E3): OHLC magnet sensitivity for `magnet` mode (`off` = no snap). */
    setCrosshairMagnet(sensitivity) {
        if (sensitivity !== "off" && sensitivity !== "weak" && sensitivity !== "normal" && sensitivity !== "strong") {
            console.warn(`[KernelEngine] Ignoring unknown crosshair magnet sensitivity: ${String(sensitivity)}`);
            return;
        }
        if (this.crosshairMagnet === sensitivity) {
            return;
        }
        this.crosshairMagnet = sensitivity;
        this.emitOverlayStateIfChanged();
        this.scheduleRender(DirtyFlag.OVERLAY);
    }
    /**
     * P2 (R-P2-04): programmatic Y-axis zoom for one scale slot.
     * Shares zoomDomainByPixels with the canvas drag path (E1) — bit-identical.
     */
    zoomYAxis(deltaYPx, options) {
        const scaleSlotId = options?.scaleSlotId ?? "price-right";
        const snapshot = this.paneSnapshots.find((s) => s.scaleSlotStates[scaleSlotId] !== undefined);
        const height = options?.heightPx ?? snapshot?.plotBounds.height ?? null;
        if (height === null || !(height > 0) || !Number.isFinite(deltaYPx) || deltaYPx === 0) {
            return;
        }
        const current = this.customSlotDomains.get(scaleSlotId) ?? snapshot?.scaleSlotStates[scaleSlotId]?.domain;
        if (!current) {
            return;
        }
        this.customSlotDomains.set(scaleSlotId, zoomDomainByPixels(current, deltaYPx, height, options?.fine === true));
        this.scheduleRender(DirtyFlag.PLOT | DirtyFlag.AXIS | DirtyFlag.OVERLAY);
        this.emitOverlayStateIfChanged();
    }
    /** P2: clear the manual Y domain for one slot, restoring auto-fit. */
    resetYAxisZoom(scaleSlotId = "price-right") {
        if (!this.customSlotDomains.has(scaleSlotId)) {
            return;
        }
        this.customSlotDomains.delete(scaleSlotId);
        this.scheduleRender(DirtyFlag.PLOT | DirtyFlag.AXIS | DirtyFlag.OVERLAY);
        this.emitOverlayStateIfChanged();
    }
    /** Q1: percent base = first visible bar close (reactive on pan/zoom). */
    resolvePercentBase() {
        const { fromIndex } = this.resolveVisibleIndexWindow(this.enrichedData.length);
        const bar = this.enrichedData[fromIndex];
        const close = bar?.close;
        return typeof close === "number" && Number.isFinite(close) && close !== 0 ? close : null;
    }
    /**
     * Resolve the effective projection spec for a scale slot.
     * Q2: log with a non-positive domain falls back to linear + warns once.
     */
    resolvePriceScaleForSlot(scaleSlotId, domain) {
        const mode = this.priceScaleController.getMode(scaleSlotId);
        const inverted = this.priceScaleController.getInverted(scaleSlotId);
        if (mode === "percent") {
            const base = this.resolvePercentBase();
            return base === null ? { mode: "linear", inverted } : { mode, base, inverted };
        }
        if (mode === "log" && domain) {
            if (!(domain[0] > 0) || !(domain[1] > 0)) {
                if (!this.priceScaleController.isLogWarned(scaleSlotId)) {
                    this.priceScaleController.addLogWarned(scaleSlotId);
                    console.warn(`[KernelEngine] priceScaleMode "log" needs a positive domain — falling back to linear for slot "${scaleSlotId}".`);
                }
                return { mode: "linear", inverted };
            }
            return { mode, inverted };
        }
        return { mode: "linear", inverted };
    }
    /**
     * @deprecated Use `setPat()` with a signed license. AUTH-01: clears any PAT so
     * a self-declared tier cannot bypass verification; only honoured when no PAT.
     */
    setPatTier(tier) {
        this.pat = "";
        this.patTier = tier;
        this.serverPatTier = null;
        this.patVerified = true;
        this.scheduleRender(DirtyFlag.OVERLAY);
    }
    // AUTH-01: a PAT prefix is NEVER authoritative. Only a host callback or a
    // signed license can grant a tier; every other outcome (throw, null, invalid
    // signature) is fail-closed to "free".
    async _validatePat(pat) {
        let resolved = "free";
        try {
            resolved = this.validatePatCallback
                ? ((await this.validatePatCallback(pat)) ?? "free")
                : (await verifyLicenseKey(pat, {
                    host: typeof window !== "undefined" ? window.location.hostname : undefined,
                }))?.tier ?? "free";
        }
        catch {
            resolved = "free";
        }
        // A newer setPat/setPatTier may have landed while we awaited.
        if (this.pat !== pat)
            return;
        this.serverPatTier = resolved;
        this.patVerified = true;
        this.emit("tierValidated", { tier: resolved });
        this.scheduleRender(DirtyFlag.OVERLAY);
    }
    /** Set raw PAT token. Always verified before it can affect the effective tier. */
    setPat(pat) {
        this.pat = pat;
        if (!pat) {
            this.patTier = "free";
            this.serverPatTier = null;
            this.patVerified = true;
            this.scheduleRender(DirtyFlag.OVERLAY);
            return;
        }
        this.patVerified = false;
        this._validatePat(pat);
    }
    getEffectivePatTier() {
        // Fail-closed while a PAT is pending verification.
        if (this.pat && !this.patVerified)
            return "free";
        return this.serverPatTier ?? this.patTier ?? "free";
    }
    getPaneSnapshots() {
        return this.paneSnapshots.map(clonePaneRuntimeSnapshot);
    }
    getVisibleRange() {
        return { ...this.visibleRange };
    }
    getOverlayState() {
        const nextOverlayState = this.computeOverlayState();
        this.overlayState = nextOverlayState;
        return cloneOverlayState(nextOverlayState);
    }
    hitTestDrawingPrimitive(localX, localY) {
        const hasPrimitives = (this.overlayInputs.drawingPrimitives && this.overlayInputs.drawingPrimitives.length > 0) ||
            typeof this.overlayInputs.drawingPrimitivesProvider === "function";
        if (this.paneSnapshots.length === 0 || !hasPrimitives) {
            return null;
        }
        const effectivePatTier = this.serverPatTier ?? this.patTier;
        const drawingPrimitives = this.computeDrawingPrimitivesState();
        for (let index = drawingPrimitives.length - 1; index >= 0; index -= 1) {
            const drawingPrimitive = drawingPrimitives[index];
            if (isPremiumDrawingBlocked(drawingPrimitive.primitive.type, effectivePatTier)) {
                continue;
            }
            const snapshot = this.paneSnapshots.find((candidate) => candidate.paneId === drawingPrimitive.paneId);
            if (!snapshot) {
                continue;
            }
            const { plotBounds } = snapshot;
            if (drawingPrimitive.clipToPane !== false
                && (localX < plotBounds.left
                    || localX > plotBounds.left + plotBounds.width
                    || localY < plotBounds.top
                    || localY > plotBounds.top + plotBounds.height)) {
                continue;
            }
            const hit = hitTestOverlayPrimitive(drawingPrimitive.primitive, localX - plotBounds.left, localY - plotBounds.top, {
                chartWidth: plotBounds.width,
                chartHeight: plotBounds.height,
                tolerance: 6,
            });
            if (hit) {
                return cloneDrawingPrimitiveState(drawingPrimitive);
            }
        }
        return null;
    }
    scrollToDataIndex(index) {
        this.gestureAnchorTo = null;
        this._zoomToRangeActive = false;
        const span = Math.max(1, this.visibleRange.to - this.visibleRange.from);
        this.visibleRange = {
            from: Math.max(0, index - span),
            to: index,
        };
        this.syncCameraStateFromVisibleRange();
        this.syncSnapshotsAndEmit();
        this.emitOverlayStateIfChanged();
        this.scheduleRender(DirtyFlag.PLOT | DirtyFlag.AXIS | DirtyFlag.OVERLAY);
        this.emit("visibleRange", { range: this.getVisibleRange(), origin: "programmatic" });
    }
    zoomToRange(range) {
        this.gestureAnchorTo = null;
        this.visibleRange = this.normalizeVisibleRange(range);
        // Preserve the programmatic range on data append — do NOT re-enable
        // followLatest even when the range sits at the right edge (prevents
        // unwanted auto-scroll that breaks multi-chart sync UX).
        this._zoomToRangeActive = true;
        this.syncCameraStateFromVisibleRange();
        this.syncSnapshotsAndEmit();
        this.emitOverlayStateIfChanged();
        this.scheduleRender(DirtyFlag.PLOT | DirtyFlag.AXIS | DirtyFlag.OVERLAY);
        this.emit("visibleRange", { range: this.getVisibleRange(), origin: "programmatic" });
    }
    subscribe(event, listener) {
        const current = this.listeners.get(event) ?? new Set();
        current.add(listener);
        this.listeners.set(event, current);
        return () => {
            current.delete(listener);
            if (current.size === 0) {
                this.listeners.delete(event);
            }
        };
    }
    dispose(reason = "manual") {
        this.interactionController?.dispose();
        this.interactionController = null;
        this.candlestickLodWorkerLane.abortAll();
        this.disposed = true;
        if (this.renderHandle !== null) {
            if (this.renderHandleKind === "raf" && typeof cancelAnimationFrame === "function") {
                cancelAnimationFrame(this.renderHandle);
            }
            else {
                clearTimeout(this.renderHandle);
            }
        }
        this.pendingRender = false;
        this.renderHandle = null;
        this.renderHandleKind = null;
        this.pageShowCleanup?.();
        this.pageShowCleanup = null;
        this.axisGridCache.invalidate();
        this.surface?.remove();
        this.surface = null;
        this.canvas = null;
        this.overlayCanvas = null;
        this.container = null;
        this.paneSnapshots = [];
        this.lastPaneSnapshotsKey = null;
        this.overlayState = {
            crosshair: null,
            hoveredPaneId: null,
            hoveredDataIndex: null,
            xAxisTicks: [],
            measurement: null,
            drawingPriceLabels: null,
            drawingPrimitives: [],
        };
        this.overlayInputs = cloneOverlayInputs(DEFAULT_OVERLAY_INPUTS);
        this.resetMeasurementState();
        this.lastOverlayStateKey = null;
        this.emit("dispose", { reason });
    }
    /**
     * KF-12-T07: Schedule a render with a reason mask.
     * Flags accumulate across multiple callers within the same frame.
     * Renderer layers may use `pendingDirtyMask` to skip unchanged layers.
     */
    scheduleRender(flags = DirtyFlag.FULL) {
        this.pendingDirtyMask |= flags;
        if (!this.surface || !this.canvas || this.disposed || this.pendingRender) {
            return;
        }
        this.pendingRender = true;
        const flush = () => this.flushScheduledRender();
        if (typeof requestAnimationFrame === "function") {
            this.renderHandleKind = "raf";
            this.renderHandle = requestAnimationFrame(flush);
            return;
        }
        this.renderHandleKind = "timeout";
        this.renderHandle = setTimeout(flush, 16);
    }
    /** KF-12-T07: Read and clear the pending dirty mask (exposed for tests and T08/T09). */
    getDirtyMask() {
        return this.pendingDirtyMask;
    }
    /** KF-12-T09: Returns axis/grid renderer call count since last reset (for test introspection). */
    getAxisGridRendererCalls() {
        return this.axisGridCache.getRendererCalls();
    }
    /** KF-12-T09: Resets the axis/grid renderer call counter. */
    resetAxisGridRendererCalls() {
        this.axisGridCache.resetRendererCalls();
    }
    /** KF-12-T08: Read the last mask consumed in flushScheduledRender (for test introspection). */
    getLastFlushedMask() {
        return this.lastFlushedMask;
    }
    flushScheduledRender() {
        this.pendingRender = false;
        this.renderHandle = null;
        this.renderHandleKind = null;
        // KF-12-T07/T08: Consume and clear the dirty mask; branch per layer
        const mask = this.pendingDirtyMask;
        this.pendingDirtyMask = DirtyFlag.NONE;
        this.lastFlushedMask = mask;
        if (this.disposed) {
            return;
        }
        // Plot / axis / grid layers — repaint main canvas
        if (mask & (DirtyFlag.PLOT | DirtyFlag.AXIS | DirtyFlag.GRID)) {
            this.refreshSurface();
        }
        // Overlay layer — repaint crosshair canvas only (skip main canvas)
        if (mask & DirtyFlag.OVERLAY) {
            this.refreshOverlay();
        }
    }
    isMounted() {
        return this.surface !== null;
    }
    resize() {
        this.refreshSurface();
    }
    emit(event, payload) {
        const listeners = this.listeners.get(event);
        if (!listeners) {
            return;
        }
        listeners.forEach((listener) => {
            listener(payload);
        });
    }
    emitInteraction(phase, context) {
        this.emit("interaction", {
            phase,
            context: { ...context },
        });
    }
    emitOverlayStateIfChanged() {
        const nextOverlayState = this.computeOverlayState();
        const overlayStateKey = JSON.stringify(nextOverlayState);
        this.overlayState = nextOverlayState;
        if (overlayStateKey === this.lastOverlayStateKey) {
            return;
        }
        this.lastOverlayStateKey = overlayStateKey;
        this.emit("overlayState", { overlayState: this.getOverlayState() });
    }
    /**
     * P2 (E3): snap a cursor Y to the nearest OHLC of the hovered bar when
     * crosshair mode is `magnet` and sensitivity is not `off`. Total function:
     * any unresolvable step returns the raw cursor Y.
     */
    resolveMagnetSnappedY(snapshot, dataIndex, cursorY) {
        if (this.crosshairMode !== "magnet" || this.crosshairMagnet === "off") {
            return cursorY;
        }
        if (dataIndex === null || dataIndex === undefined) {
            return cursorY;
        }
        const ohlc = readOhlc(this.enrichedData[dataIndex]);
        if (!ohlc) {
            return cursorY;
        }
        const states = snapshot.scaleSlotStates;
        const ids = Object.keys(states);
        const priceSlotId = ids.find((id) => id.toLowerCase().includes("price"))
            ?? ids.find((id) => states[id]?.side === "right")
            ?? ids[0];
        if (!priceSlotId) {
            return cursorY;
        }
        const domain = this.customSlotDomains.get(priceSlotId) ?? states[priceSlotId]?.domain;
        if (!domain) {
            return cursorY;
        }
        const projector = createPaneProjector(snapshot, this.visibleRange, priceSlotId, this.resolvePriceScaleForSlot(priceSlotId, domain));
        return snapCrosshairYToOHLC({
            cursorY,
            ohlc,
            priceToY: (price) => projector.valueToPixelY(price),
            sensitivity: this.crosshairMagnet,
        }).y;
    }
    computeOverlayState() {
        const hoveredSnapshot = this.resolveCrosshairSnapshot();
        const hoveredDataIndex = hoveredSnapshot && this.crosshairPos
            ? resolvePaneDataIndexAtPixel(hoveredSnapshot, this.visibleRange, this.crosshairPos.x, this.enrichedData.length)
            : null;
        const hoveredXIndexFloat = hoveredSnapshot && this.crosshairPos
            ? createPaneProjector(hoveredSnapshot, this.visibleRange, "__unused-scale-slot__").pixelXToIndex(this.crosshairPos.x)
            : null;
        // P2 (E3): magnet mode snaps the reported Y to the hovered-bar OHLC.
        const crosshairY = hoveredSnapshot && this.crosshairPos
            ? this.resolveMagnetSnappedY(hoveredSnapshot, hoveredDataIndex, this.crosshairPos.y)
            : this.crosshairPos?.y ?? 0;
        return {
            crosshair: this.crosshairPos
                ? {
                    x: this.crosshairPos.x,
                    y: crosshairY,
                    paneId: hoveredSnapshot?.paneId ?? null,
                    dataIndex: hoveredDataIndex,
                    xIndexFloat: hoveredXIndexFloat,
                }
                : null,
            hoveredPaneId: hoveredSnapshot?.paneId ?? null,
            hoveredDataIndex,
            xAxisTicks: this.computeOverlayXAxisTicks(),
            measurement: this.computeMeasurementOverlayState(),
            drawingPriceLabels: this.computeDrawingPriceLabelsState(),
            drawingPrimitives: this.computeDrawingPrimitivesState(),
        };
    }
    projectDrawingPrimitive(primitive, snapshot, scaleSlotId, actionHandle) {
        const projector = createPaneProjector(snapshot, this.visibleRange, scaleSlotId, this.resolvePriceScaleForSlot(scaleSlotId, snapshot.scaleSlotStates[scaleSlotId]?.domain));
        const { left, top } = snapshot.plotBounds;
        const projectPoint = (point) => {
            const nextPoint = { ...point };
            if (typeof point.xIndexFloat === "number" && Number.isFinite(point.xIndexFloat)) {
                const px = projector.indexToPixelX(point.xIndexFloat + 0.5);
                if (px !== null) {
                    nextPoint.x = px - left;
                }
            }
            if (typeof point.yPrice === "number" && Number.isFinite(point.yPrice)) {
                const py = projector.valueToPixelY(point.yPrice);
                if (py !== null) {
                    nextPoint.y = py - top;
                }
            }
            return nextPoint;
        };
        const nextPoints = primitive.points.map(projectPoint);
        if (actionHandle && nextPoints.length >= 2 && nextPoints[0]) {
            nextPoints[1] = { x: nextPoints[0].x + 8, y: nextPoints[0].y };
        }
        const nextSegments = primitive.segments?.map((segment) => ({
            ...segment,
            start: projectPoint(segment.start),
            end: projectPoint(segment.end),
            labelPosition: segment.labelPosition ? projectPoint(segment.labelPosition) : undefined,
        }));
        const nextPaths = primitive.paths?.map((path) => ({
            ...path,
            points: path.points.map(projectPoint),
        }));
        const nextPointMarkers = primitive.pointMarkers?.map((marker) => ({
            ...marker,
            point: projectPoint(marker.point),
        }));
        const nextLabels = primitive.labels?.map((label) => {
            const position = projectPoint(label.position);
            return {
                ...label,
                position: actionHandle && nextPoints[0] ? nextPoints[0] : position,
            };
        });
        const nextPolygons = primitive.polygons?.map((polygon) => ({
            ...polygon,
            points: polygon.points.map(projectPoint),
        }));
        const nextSemanticBadges = primitive.semanticBadges?.map((badge) => ({
            ...badge,
            position: projectPoint(badge.position),
        }));
        const nextLevelLines = primitive.levelLines?.map((level) => {
            if (typeof level.yPrice === "number" && Number.isFinite(level.yPrice)) {
                const py = projector.valueToPixelY(level.yPrice);
                if (py !== null) {
                    return { ...level, y: py - top };
                }
            }
            return level;
        });
        const nextVerticalMarkers = primitive.verticalMarkers?.map((marker) => {
            if (typeof marker.xIndexFloat === "number" && Number.isFinite(marker.xIndexFloat)) {
                const px = projector.indexToPixelX(marker.xIndexFloat + 0.5);
                if (px !== null) {
                    return { ...marker, x: px - left };
                }
            }
            return marker;
        });
        let nextBaseRadius = 0;
        if (nextPoints.length >= 2 && nextPoints[0] && nextPoints[1]) {
            nextBaseRadius = Math.hypot(nextPoints[1].x - nextPoints[0].x, nextPoints[1].y - nextPoints[0].y);
        }
        const nextArcLevels = primitive.arcLevels?.map((level) => {
            let nextRadius = level.radius;
            if (typeof level.radiusRatio === "number" && Number.isFinite(level.radiusRatio) && nextBaseRadius > 0) {
                nextRadius = nextBaseRadius * level.radiusRatio;
            }
            let nextLabelPos = level.labelPosition;
            if (nextLabelPos && typeof nextLabelPos.xIndexFloat === "number" && typeof nextLabelPos.yPrice === "number") {
                nextLabelPos = projectPoint(nextLabelPos);
            }
            else if (nextLabelPos && nextPoints[0] && nextBaseRadius > 0 && typeof level.radiusRatio === "number") {
                nextLabelPos = {
                    x: nextPoints[0].x + nextRadius * 0.707,
                    y: nextPoints[0].y - nextRadius * 0.707,
                };
            }
            return {
                ...level,
                radius: nextRadius,
                labelPosition: nextLabelPos,
            };
        });
        return {
            ...primitive,
            points: nextPoints,
            segments: nextSegments,
            paths: nextPaths,
            pointMarkers: nextPointMarkers,
            labels: nextLabels,
            polygons: nextPolygons,
            semanticBadges: nextSemanticBadges,
            levelLines: nextLevelLines,
            verticalMarkers: nextVerticalMarkers,
            arcLevels: nextArcLevels,
        };
    }
    computeDrawingPrimitivesState() {
        if (this.paneSnapshots.length === 0) {
            return [];
        }
        let primitives = this.overlayInputs.drawingPrimitives;
        if (this.overlayInputs.drawingPrimitivesProvider) {
            primitives = this.overlayInputs.drawingPrimitivesProvider(this.visibleRange, this.paneSnapshots);
        }
        if (!primitives || primitives.length === 0) {
            return [];
        }
        return primitives
            .filter((primitive) => primitive.primitive.visible !== false && this.paneSnapshots.some((snapshot) => snapshot.paneId === primitive.paneId))
            .map((primitive) => {
            const snapshot = this.paneSnapshots.find((s) => s.paneId === primitive.paneId);
            const scaleSlotId = primitive.scaleSlotId || Object.keys(snapshot.scaleSlotStates)[0];
            const projected = this.projectDrawingPrimitive(primitive.primitive, snapshot, scaleSlotId, primitive.actionHandle);
            return {
                key: primitive.key,
                paneId: primitive.paneId,
                scaleSlotId: primitive.scaleSlotId,
                primitive: projected,
                selected: primitive.selected,
                drawingId: primitive.drawingId,
                actionHandle: primitive.actionHandle,
                clipToPane: primitive.clipToPane,
            };
        });
    }
    resolveCrosshairSnapshot() {
        if (!this.crosshairPos) {
            return null;
        }
        return this.paneSnapshots.find((snapshot) => {
            const { plotBounds } = snapshot;
            return this.crosshairPos !== null
                && this.crosshairPos.x >= plotBounds.left
                && this.crosshairPos.x <= plotBounds.left + plotBounds.width
                && this.crosshairPos.y >= plotBounds.top
                && this.crosshairPos.y <= plotBounds.top + plotBounds.height;
        }) ?? null;
    }
    computeOverlayXAxisTicks() {
        if (this.paneSnapshots.length === 0 || this.enrichedData.length === 0) {
            return [];
        }
        const bottomSnapshot = this.paneSnapshots[this.paneSnapshots.length - 1];
        if (!bottomSnapshot) {
            return [];
        }
        const fromIndex = clampOverlayIndex(Math.min(this.visibleRange.from, this.visibleRange.to), this.enrichedData.length);
        const toIndex = clampOverlayIndex(Math.max(this.visibleRange.from, this.visibleRange.to), this.enrichedData.length);
        const tickCount = Math.max(2, Math.min(8, Math.floor(bottomSnapshot.plotBounds.width / 80)));
        const seenIndices = new Set();
        const ticks = [];
        for (let tickIndex = 0; tickIndex < tickCount; tickIndex += 1) {
            const ratio = tickCount === 1 ? 0 : tickIndex / (tickCount - 1);
            const dataIndex = clampOverlayIndex(Math.round(fromIndex + (toIndex - fromIndex) * ratio), this.enrichedData.length);
            if (seenIndices.has(dataIndex)) {
                continue;
            }
            seenIndices.add(dataIndex);
            const x = resolvePanePixelAtDataIndex(bottomSnapshot, this.visibleRange, dataIndex, this.enrichedData.length);
            if (x === null) {
                continue;
            }
            ticks.push({
                key: `${bottomSnapshot.paneId}-${dataIndex}`,
                x,
                dataIndex,
            });
        }
        return ticks;
    }
    resetMeasurementState() {
        this.measurementHoverPoint = null;
        this.measurementDraft = null;
        this.measurementCommitted = null;
    }
    handleMeasurementPointerDown(context) {
        if (!this.overlayInputs.measurementEnabled) {
            return;
        }
        const nextPoint = this.resolveMeasurementLogicalPoint(context.localX, context.localY);
        if (!nextPoint) {
            return;
        }
        this.measurementHoverPoint = cloneLogicalMeasurementPoint(nextPoint);
        this.measurementCommitted = null;
        this.measurementDraft = {
            paneId: nextPoint.paneId,
            scaleSlotId: nextPoint.scaleSlotId,
            start: cloneLogicalMeasurementPoint(nextPoint),
            end: cloneLogicalMeasurementPoint(nextPoint),
        };
        this.emitOverlayStateIfChanged();
    }
    handleMeasurementPointerMove(context) {
        if (!this.overlayInputs.measurementEnabled) {
            return;
        }
        if (this.measurementDraft) {
            const nextPoint = this.resolveMeasurementLogicalPoint(context.localX, context.localY, this.measurementDraft.paneId, this.measurementDraft.scaleSlotId);
            if (!nextPoint) {
                return;
            }
            this.measurementHoverPoint = cloneLogicalMeasurementPoint(nextPoint);
            this.measurementDraft = {
                paneId: this.measurementDraft.paneId,
                scaleSlotId: this.measurementDraft.scaleSlotId,
                start: cloneLogicalMeasurementPoint(this.measurementDraft.start),
                end: cloneLogicalMeasurementPoint(nextPoint),
            };
            this.emitOverlayStateIfChanged();
            return;
        }
        const hoverPoint = this.resolveMeasurementLogicalPoint(context.localX, context.localY);
        this.measurementHoverPoint = hoverPoint ? cloneLogicalMeasurementPoint(hoverPoint) : null;
        this.emitOverlayStateIfChanged();
    }
    handleMeasurementPointerCancel() {
        if (!this.overlayInputs.measurementEnabled) {
            return;
        }
        this.measurementHoverPoint = null;
        this.measurementDraft = null;
        this.emitOverlayStateIfChanged();
    }
    handleMeasurementPointerLeave() {
        if (!this.overlayInputs.measurementEnabled) {
            return;
        }
        this.measurementHoverPoint = null;
        this.commitMeasurementDraft();
    }
    handleMeasurementClick() {
        if (!this.overlayInputs.measurementEnabled) {
            return;
        }
        this.commitMeasurementDraft();
    }
    commitMeasurementDraft() {
        if (!this.measurementDraft) {
            this.emitOverlayStateIfChanged();
            return;
        }
        this.measurementCommitted = cloneLogicalMeasurementSelection(this.measurementDraft);
        this.measurementDraft = null;
        this.emitOverlayStateIfChanged();
    }
    resolveMeasurementSnapshot(localX, localY, activePaneId) {
        if (activePaneId) {
            return this.paneSnapshots.find((snapshot) => snapshot.paneId === activePaneId) ?? null;
        }
        return this.paneSnapshots.find((snapshot) => {
            const { plotBounds } = snapshot;
            return localX >= plotBounds.left
                && localX <= plotBounds.left + plotBounds.width
                && localY >= plotBounds.top
                && localY <= plotBounds.top + plotBounds.height;
        }) ?? null;
    }
    resolveMeasurementLogicalPoint(localX, localY, activePaneId, activeScaleSlotId) {
        const snapshot = this.resolveMeasurementSnapshot(localX, localY, activePaneId);
        if (!snapshot) {
            return undefined;
        }
        const scaleSlotId = resolvePreferredMeasurementScaleSlotId(snapshot, activeScaleSlotId);
        if (!scaleSlotId) {
            return undefined;
        }
        const index = resolvePaneDataIndexAtPixel(snapshot, this.visibleRange, localX, this.enrichedData.length);
        const price = resolvePaneValueAtPixel(snapshot, scaleSlotId, localY);
        if (index === null || price === null) {
            return undefined;
        }
        return {
            paneId: snapshot.paneId,
            scaleSlotId,
            index,
            price,
        };
    }
    resolveOverlayMeasurementPoint(point) {
        const snapshot = this.paneSnapshots.find((candidate) => candidate.paneId === point.paneId);
        if (!snapshot) {
            return null;
        }
        const scaleSlotId = resolvePreferredMeasurementScaleSlotId(snapshot, point.scaleSlotId);
        if (!scaleSlotId) {
            return null;
        }
        const x = resolvePanePixelAtDataIndex(snapshot, this.visibleRange, point.index, this.enrichedData.length);
        const y = resolvePanePixelAtValue(snapshot, scaleSlotId, point.price);
        if (x === null || y === null) {
            return null;
        }
        return {
            paneId: snapshot.paneId,
            scaleSlotId,
            index: point.index,
            price: point.price,
            x,
            y,
        };
    }
    resolveOverlayMeasurementSelection(selection) {
        const start = this.resolveOverlayMeasurementPoint(selection.start);
        const end = this.resolveOverlayMeasurementPoint(selection.end);
        if (!start || !end) {
            return null;
        }
        return {
            paneId: selection.paneId,
            scaleSlotId: selection.scaleSlotId,
            start,
            end,
        };
    }
    computeMeasurementOverlayState() {
        if (!this.overlayInputs.measurementEnabled) {
            return null;
        }
        return {
            enabled: true,
            hoverPoint: this.measurementHoverPoint ? this.resolveOverlayMeasurementPoint(this.measurementHoverPoint) : null,
            draft: this.measurementDraft ? this.resolveOverlayMeasurementSelection(this.measurementDraft) : null,
            committed: this.measurementCommitted ? this.resolveOverlayMeasurementSelection(this.measurementCommitted) : null,
        };
    }
    computeDrawingPriceLabelsState() {
        const selectedPriceLabels = this.overlayInputs.selectedPriceLabels;
        if (!selectedPriceLabels || selectedPriceLabels.visible === false) {
            return null;
        }
        const snapshot = this.paneSnapshots.find((candidate) => candidate.paneId === selectedPriceLabels.paneId);
        if (!snapshot) {
            return null;
        }
        const scaleSlotId = resolvePreferredPriceLabelScaleSlotId(snapshot, selectedPriceLabels.scaleSlotId);
        if (!scaleSlotId) {
            return null;
        }
        const side = snapshot.scaleSlotStates[scaleSlotId]?.side ?? "right";
        const labels = selectedPriceLabels.markers.flatMap((marker) => {
            const y = resolvePanePixelAtValue(snapshot, scaleSlotId, marker.price);
            if (y === null) {
                return [];
            }
            return [{
                    key: marker.key,
                    price: marker.price,
                    y,
                    side: side,
                    fill: selectedPriceLabels.fill,
                    stroke: selectedPriceLabels.stroke,
                    textColor: selectedPriceLabels.textColor,
                }];
        });
        if (labels.length === 0) {
            return null;
        }
        return {
            drawingId: selectedPriceLabels.drawingId,
            paneId: snapshot.paneId,
            scaleSlotId,
            highlighted: selectedPriceLabels.highlighted,
            labels,
        };
    }
    emitPaneSnapshotsIfChanged() {
        const paneSnapshotsKey = JSON.stringify(this.paneSnapshots);
        if (paneSnapshotsKey === this.lastPaneSnapshotsKey) {
            return;
        }
        this.lastPaneSnapshotsKey = paneSnapshotsKey;
        this.emit("panes", {
            paneCount: this.paneSnapshots.length,
            snapshots: this.getPaneSnapshots(),
        });
    }
    syncSnapshotsAndEmit() {
        if (this.paneSnapshots.length === 0) {
            return;
        }
        const effectivePanes = this.priceScaleController.withSidesApplied(this.paneSpecs);
        for (let paneIndex = 0; paneIndex < this.paneSnapshots.length; paneIndex += 1) {
            const snapshot = this.paneSnapshots[paneIndex];
            const paneSpec = effectivePanes[paneIndex];
            if (!snapshot || !paneSpec) {
                continue;
            }
            const slotDomains = this.applyPaneScaleSlotDomains(paneSpec, snapshot);
            this.paneLayout.reconcileEmptyLeftAxis(snapshot, paneSpec, slotDomains);
        }
        this.emitPaneSnapshotsIfChanged();
    }
    refreshSurface() {
        if (!this.surface || !this.canvas) {
            return;
        }
        const width = Math.max(1, this.container?.clientWidth || DEFAULT_WIDTH);
        const height = Math.max(1, this.container?.clientHeight || DEFAULT_HEIGHT);
        // DPR-aware backing store (recomputed every paint: cheap, and tracks
        // monitor moves across displays with different scale factors).
        this.devicePixelRatio = this.resolveDevicePixelRatio();
        const dpr = this.devicePixelRatio;
        const backingWidth = Math.max(1, Math.round(width * dpr));
        const backingHeight = Math.max(1, Math.round(height * dpr));
        // Guard canvas dimension changes to avoid destroying the GPU backing store
        if (this.canvas.width !== backingWidth) {
            this.canvas.width = backingWidth;
        }
        if (this.canvas.height !== backingHeight) {
            this.canvas.height = backingHeight;
        }
        // KF-12-T08: Keep overlay canvas dimensions in sync with main canvas
        if (this.overlayCanvas) {
            if (this.overlayCanvas.width !== backingWidth) {
                this.overlayCanvas.width = backingWidth;
            }
            if (this.overlayCanvas.height !== backingHeight) {
                this.overlayCanvas.height = backingHeight;
            }
        }
        this.surface.dataset.theme = this.theme.mode;
        this.surface.dataset.locale = this.locale;
        if (this.dataSource?.sourceId) {
            this.surface.dataset.sourceId = this.dataSource.sourceId;
        }
        else {
            delete this.surface.dataset.sourceId;
        }
        const paneAreaHeight = Math.max(1, height - PANEL_HEADER_HEIGHT);
        const effectivePanes = this.priceScaleController.withSidesApplied(this.paneSpecs);
        this.paneSnapshots = this.paneLayout.layout(effectivePanes, width, paneAreaHeight);
        for (let paneIndex = 0; paneIndex < this.paneSnapshots.length; paneIndex += 1) {
            const snapshot = this.paneSnapshots[paneIndex];
            const paneSpec = effectivePanes[paneIndex];
            if (!snapshot || !paneSpec) {
                continue;
            }
            const slotDomains = this.applyPaneScaleSlotDomains(paneSpec, snapshot);
            this.paneLayout.reconcileEmptyLeftAxis(snapshot, paneSpec, slotDomains);
        }
        this.emitOverlayStateIfChanged();
        const context = this.canvas.getContext("2d", { willReadFrequently: true });
        if (!context) {
            this.emitPaneSnapshotsIfChanged();
            return;
        }
        // Canvas width/height assignment resets context state — reinstall the
        // DPR transform on every paint so all drawing stays in CSS pixels.
        context.setTransform(this.devicePixelRatio, 0, 0, this.devicePixelRatio, 0, 0);
        context.clearRect(0, 0, width, height);
        this.renderPaneSeries(context);
        this.emitPaneSnapshotsIfChanged();
        this.emitOverlayStateIfChanged();
    }
    renderPaneSeries(ctx) {
        const effectivePanes = this.priceScaleController.withSidesApplied(this.paneSpecs);
        for (let paneIndex = 0; paneIndex < this.paneSnapshots.length; paneIndex += 1) {
            const snapshot = this.paneSnapshots[paneIndex];
            const paneSpec = effectivePanes[paneIndex];
            if (!snapshot || !paneSpec) {
                continue;
            }
            const plotBounds = snapshot.plotBounds;
            const slotDomains = this.applyPaneScaleSlotDomains(paneSpec, snapshot);
            const leftSlot = paneSpec.scaleSlots.find((slot) => slot.side === "left" && slot.visible);
            const rightSlot = paneSpec.scaleSlots.find((slot) => slot.side === "right" && slot.visible);
            // 5b-ii: per-slot projection spec (default linear; indicator slots unaffected).
            const leftAxisScale = leftSlot ? this.resolvePriceScaleForSlot(leftSlot.id, slotDomains.get(leftSlot.id)) : undefined;
            const rightAxisScale = rightSlot ? this.resolvePriceScaleForSlot(rightSlot.id, slotDomains.get(rightSlot.id)) : undefined;
            const leftAxis = snapshot.axisBounds.left
                ? {
                    bounds: snapshot.axisBounds.left,
                    domain: leftSlot ? slotDomains.get(leftSlot.id) : undefined,
                    scale: leftAxisScale,
                }
                : undefined;
            const rightAxis = snapshot.axisBounds.right
                ? {
                    bounds: snapshot.axisBounds.right,
                    domain: rightSlot ? slotDomains.get(rightSlot.id) : undefined,
                    scale: rightAxisScale,
                }
                : undefined;
            const visibleBarRange = this.enrichedData.length > 0
                ? { from: Math.max(0, Math.round(this.visibleRange.from)), to: Math.min(this.enrichedData.length, Math.round(this.visibleRange.to)) }
                : undefined;
            const axisGridTheme = axisGridThemeInputs(this.theme);
            const axisCacheKey = buildAxisGridCacheKey({
                paneId: paneSpec.id,
                leftDomain: leftAxis?.domain,
                rightDomain: rightAxis?.domain,
                leftScale: leftAxisScale ? `${leftAxisScale.mode}:${leftAxisScale.base ?? ""}` : undefined,
                rightScale: rightAxisScale ? `${rightAxisScale.mode}:${rightAxisScale.base ?? ""}` : undefined,
                themeMode: this.theme.mode,
                presetName: this.theme.presetName,
                width: plotBounds.width + (leftAxis?.bounds.width ?? 0) + (rightAxis?.bounds.width ?? 0),
                height: plotBounds.height,
                barFrom: visibleBarRange?.from,
                barTo: visibleBarRange?.to,
                labelColor: this.theme.axisLabelColor,
                gridColor: this.theme.gridColor,
                gridSubtleColor: this.theme.gridSubtleColor,
                ...axisGridTheme.cacheKey,
                gridDash: this.theme.gridDash,
                gridWidth: this.theme.gridWidth,
            });
            this.axisGridCache.renderWithCache(ctx, {
                plotBounds,
                themeMode: this.theme.mode,
                presetName: this.theme.presetName,
                leftAxis,
                rightAxis,
                visibleBarRange,
                labelColor: this.theme.axisLabelColor,
                gridColor: this.theme.gridColor,
                gridSubtleColor: this.theme.gridSubtleColor,
                ...axisGridTheme.options,
                gridDash: this.theme.gridDash,
                gridWidth: this.theme.gridWidth,
                axisLabelFormatters: this.axisLabelFormatters,
            }, axisCacheKey);
            ctx.save();
            ctx.beginPath();
            ctx.rect(plotBounds.left, plotBounds.top, plotBounds.width, plotBounds.height);
            ctx.clip();
            for (const series of paneSpec.series) {
                if (series.visible === false) {
                    continue;
                }
                if (series.type === "BollingerBand") {
                    this.renderBollingerBandFill(ctx, snapshot, paneSpec, series, plotBounds, slotDomains);
                }
                if (series.type === "RSI" || series.type === "MACD") {
                    this.renderSeriesReferenceLines(ctx, series, plotBounds, slotDomains);
                }
                const visibleBarCount = this.resolveVisibleBarCount();
                // Candlestick series: bypass scalar path, use CandlestickRenderer with OHLCV enrichedData
                if (this.isCandlestickSeries(series)) {
                    this.renderCandlestickSeriesIntoPane(ctx, snapshot, series, plotBounds, slotDomains);
                    continue;
                }
                for (const { spec: subSeries } of this.expandSeriesIntoSubLines(series)) {
                    const values = this.resolveSeriesValues(subSeries);
                    if (values.length === 0) {
                        continue;
                    }
                    const mode = this.resolveSeriesMode(subSeries);
                    const slotDomain = slotDomains.get(subSeries.scaleSlotId)
                        ?? this.createDomainFromSeriesValues(values, isZeroFlooredMode(mode));
                    const yScale = this.createYScaleFromDomain(slotDomain, plotBounds.top, plotBounds.height, this.resolvePriceScaleForSlot(subSeries.scaleSlotId, slotDomain));
                    const bucketProjector = createPaneProjector(snapshot, this.visibleRange, subSeries.scaleSlotId);
                    const { fromIndex } = this.resolveVisibleIndexWindow(this.enrichedData.length);
                    const overlayLodBucketSize = this.resolveScalarOverlayLodBucketSize(paneSpec, subSeries.scaleSlotId, plotBounds.width);
                    if (isRectangleMode(mode)) {
                        const configuredBarFill = this.resolveConfiguredSeriesColor(subSeries, "fill");
                        // Renko needs a per-bar baseline + direction palette; `bar` keeps
                        // the zero-based behaviour, so nothing is allocated for it.
                        const isRenko = isRenkoMode(mode);
                        const points = this.toBarPoints(values, bucketProjector, fromIndex, configuredBarFill
                            ? undefined
                            : (this.resolveDefaultBarPalette(subSeries, values)
                                ?? (isRenko ? resolveRectanglePalette(this.enrichedData, fromIndex, values.length) : undefined)), isRenko ? resolveRectangleBaselines(this.enrichedData, fromIndex, values.length) : undefined);
                        this.barRenderer.render(ctx, points, {
                            yScale,
                            baselineValue: 0,
                            barWidth: this.resolveBarWidth(visibleBarCount, plotBounds.width),
                            fill: configuredBarFill ?? ((datum) => datum.paletteColor ?? "#6BA583"),
                        });
                        continue;
                    }
                    if (isColumnStackMode(mode)) {
                        // P&F: một rect cho MỖI box, x lấy từ CỘT. Không đi qua
                        // `toBarPoints` — hàm đó đặt mỗi bar một x riêng ⇒ cột bị nhoè.
                        const layout = layoutPointAndFigure({
                            data: this.enrichedData,
                            fromIndex,
                            count: values.length,
                            indexToPixelX: (index) => bucketProjector.indexToPixelX(index),
                            plotWidth: plotBounds.width,
                        });
                        this.barRenderer.render(ctx, layout.rects, {
                            yScale,
                            baselineValue: 0,
                            barWidth: layout.columnWidth,
                            fill: (datum) => datum.paletteColor ?? PNF_UP,
                        });
                        continue;
                    }
                    const points = this.toLineAreaPoints(values, bucketProjector, fromIndex, overlayLodBucketSize);
                    // CT-P0: area fill always reaches the plot bottom with a vertical
                    // gradient; an explicitly configured fill keeps flat behavior.
                    const isAreaMode = mode === "area";
                    const configuredAreaFill = isAreaMode ? this.resolveConfiguredSeriesColor(subSeries, "fill") : undefined;
                    this.lineAreaRenderer.render(ctx, points, {
                        mode: isAreaMode ? "area" : "line",
                        curve: isStepMode(mode) ? "stepBefore" : "linear",
                        useWebGL: this.options.useWebGL,
                        yScale,
                        baselineValue: undefined,
                        stroke: this.resolveSeriesStroke(subSeries),
                        fill: configuredAreaFill ?? this.resolveSeriesFill(subSeries),
                        lineWidth: this.resolveSeriesLineWidth(subSeries, visibleBarCount, plotBounds.width),
                        areaBottomY: isAreaMode ? plotBounds.top + plotBounds.height : undefined,
                        areaGradient: isAreaMode && !configuredAreaFill
                            ? ["rgba(41,98,255,0.30)", "rgba(41,98,255,0)"]
                            : undefined,
                    });
                }
            }
            ctx.restore();
            // Price line + last value badge (GoCharting / TradingView parity)
            // A4: `lastValue` defaults to ON (it was drawn unconditionally before the
            // option existed) and `prevClose` defaults to OFF (that line is new).
            this.priceLineOverlay.render(ctx, {
                plotBounds,
                rightAxisBounds: rightAxis?.bounds,
                leftAxisBounds: leftAxis?.bounds,
                priceSlotSide: resolvePriceSlotSide(paneSpec, (s) => this.isCandlestickSeries(s)),
                series: paneSpec.series,
                findPriceSeries: (s) => this.isCandlestickSeries(s) || s.id === "price" || s.id === "candles" || (s.scaleSlotId === "price-right" && isPriceLevelMode(this.resolveSeriesMode(s))),
                bars: this.enrichedData,
                slotDomains,
                yScaleFor: (slotId, domain) => this.createYScaleFromDomain(domain, plotBounds.top, plotBounds.height, this.resolvePriceScaleForSlot(slotId, domain)),
                bullColor: this.theme.candleUp ?? "#089981",
                bearColor: this.theme.candleDown ?? "#f23645",
                mutedColor: this.theme.axisLabelColor ?? "#787b86",
                lastValue: this.options.priceLines?.lastValue !== false,
                prevCloseLine: this.options.priceLines?.prevClose === true,
            });
            // KF-12-T08: Crosshair moved to refreshOverlay() — no longer rendered on main canvas
        }
    }
    resolveBollingerFillMode(series) {
        const rawMode = series.params?.["fillMode"];
        if (rawMode === "solid" || rawMode === "fade" || rawMode === "none") {
            return rawMode;
        }
        if (rawMode === "gradient") {
            return "fade";
        }
        return "none";
    }
    resolveSeriesFillOpacity(series) {
        const rawOpacity = series.params?.["fillOpacity"];
        if (typeof rawOpacity !== "number") {
            return 0.15;
        }
        return clampUnit(rawOpacity, 0.15);
    }
    resolveSeriesFillBaseColor(series) {
        const rawFillColor = series.params?.["fillColor"];
        if (typeof rawFillColor === "string" && rawFillColor.length > 0) {
            return rawFillColor;
        }
        return this.resolveConfiguredSeriesColor(series, "fill")
            ?? this.resolveConfiguredSeriesColor(series, "stroke")
            ?? "#2962ff";
    }
    renderBollingerBandFill(ctx, snapshot, paneSpec, series, plotBounds, slotDomains) {
        const fillMode = this.resolveBollingerFillMode(series);
        if (fillMode === "none") {
            return;
        }
        const upperValues = this.resolveSeriesValues({ ...series, type: "BollingerBand.upper" });
        const lowerValues = this.resolveSeriesValues({ ...series, type: "BollingerBand.lower" });
        if (upperValues.length === 0 || lowerValues.length === 0) {
            return;
        }
        const combinedValues = [...upperValues, ...lowerValues].filter(Number.isFinite);
        if (combinedValues.length === 0) {
            return;
        }
        const slotDomain = slotDomains.get(series.scaleSlotId)
            ?? this.createDomainFromSeriesValues(combinedValues, false);
        const yScale = this.createYScaleFromDomain(slotDomain, plotBounds.top, plotBounds.height, this.resolvePriceScaleForSlot(series.scaleSlotId, slotDomain));
        const bucketProjector = createPaneProjector(snapshot, this.visibleRange, series.scaleSlotId);
        const { fromIndex } = this.resolveVisibleIndexWindow(this.enrichedData.length);
        const overlayLodBucketSize = this.resolveScalarOverlayLodBucketSize(paneSpec, series.scaleSlotId, plotBounds.width);
        const upperPoints = this.toLineAreaPoints(upperValues, bucketProjector, fromIndex, overlayLodBucketSize)
            .map((point) => ({ x: point.x, y: yScale(point.value) }));
        const lowerPoints = this.toLineAreaPoints(lowerValues, bucketProjector, fromIndex, overlayLodBucketSize)
            .map((point) => ({ x: point.x, y: yScale(point.value) }));
        if (upperPoints.length < 2 || lowerPoints.length < 2) {
            return;
        }
        const fillOpacity = this.resolveSeriesFillOpacity(series);
        const baseColor = this.resolveSeriesFillBaseColor(series);
        const fillStyle = fillMode === "fade"
            ? (() => {
                let minY = plotBounds.top;
                let maxY = plotBounds.top + plotBounds.height;
                if (upperPoints.length > 0 && lowerPoints.length > 0) {
                    minY = Math.min(...upperPoints.map((p) => p.y));
                    maxY = Math.max(...lowerPoints.map((p) => p.y));
                }
                const gradient = ctx.createLinearGradient(0, minY, 0, maxY);
                gradient.addColorStop(0, withAlpha(baseColor, 0));
                gradient.addColorStop(0.5, withAlpha(baseColor, fillOpacity));
                gradient.addColorStop(1, withAlpha(baseColor, 0));
                return gradient;
            })()
            : withAlpha(baseColor, fillOpacity);
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(upperPoints[0].x, upperPoints[0].y);
        for (let index = 1; index < upperPoints.length; index += 1) {
            ctx.lineTo(upperPoints[index].x, upperPoints[index].y);
        }
        for (let index = lowerPoints.length - 1; index >= 0; index -= 1) {
            ctx.lineTo(lowerPoints[index].x, lowerPoints[index].y);
        }
        ctx.closePath();
        ctx.fillStyle = fillStyle;
        ctx.fill();
        ctx.restore();
    }
    renderSeriesReferenceLines(ctx, series, plotBounds, slotDomains) {
        const slotDomain = slotDomains.get(series.scaleSlotId);
        if (!slotDomain) {
            return;
        }
        const yScale = this.createYScaleFromDomain(slotDomain, plotBounds.top, plotBounds.height, this.resolvePriceScaleForSlot(series.scaleSlotId, slotDomain));
        if (series.type === "RSI") {
            const levels = [
                { value: 70, key: "overbought", defaultColor: "#f23645", dash: true },
                { value: 50, key: "middle", defaultColor: "#9ca3af", dash: true },
                { value: 30, key: "oversold", defaultColor: "#089981", dash: true },
            ];
            ctx.save();
            for (const level of levels) {
                const y = yScale(level.value);
                if (y >= plotBounds.top && y <= plotBounds.top + plotBounds.height) {
                    ctx.strokeStyle = series.style?.subColors?.[level.key] ?? level.defaultColor;
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    if (level.dash) {
                        ctx.setLineDash([4, 4]);
                    }
                    else {
                        ctx.setLineDash([]);
                    }
                    ctx.moveTo(plotBounds.left, y);
                    ctx.lineTo(plotBounds.left + plotBounds.width, y);
                    ctx.stroke();
                }
            }
            ctx.restore();
        }
        else if (series.type === "MACD") {
            const y = yScale(0);
            if (y >= plotBounds.top && y <= plotBounds.top + plotBounds.height) {
                ctx.save();
                ctx.strokeStyle = "rgba(0, 0, 0, 0.3)";
                ctx.lineWidth = 1;
                ctx.setLineDash([]);
                ctx.beginPath();
                ctx.moveTo(plotBounds.left, y);
                ctx.lineTo(plotBounds.left + plotBounds.width, y);
                ctx.stroke();
                ctx.restore();
            }
        }
    }
    applyPaneScaleSlotDomains(pane, snapshot) {
        const slotDomains = this.computePaneScaleSlotDomains(pane);
        for (const slot of pane.scaleSlots) {
            const domain = slotDomains.get(slot.id);
            if (!domain) {
                continue;
            }
            snapshot.scaleSlotStates[slot.id] = {
                ...(snapshot.scaleSlotStates[slot.id] ?? {
                    side: slot.side,
                    mode: slot.mode,
                    visible: slot.visible,
                }),
                domain,
            };
        }
        return slotDomains;
    }
    /**
     * CT-P2b: beacon point for Line/Area price series at the hovered bar.
     * Returns null when there is no hover, no Line/Area series, or no domain
     * (volume/indicator panes exit early). y mapping reuses the slot domain
     * stored on the snapshot by applyPaneScaleSlotDomains.
     */
    resolvePriceBeacon(snapshot, x, dataIndex) {
        if (dataIndex === null || dataIndex === undefined) {
            return null;
        }
        const pane = this.paneSpecs.find((p) => p.id === snapshot.paneId);
        const priceSeries = pane?.series.find((s) => s.visible !== false && (s.type === "Line" || s.type === "Area"));
        if (!priceSeries) {
            return null;
        }
        const window = this.resolveVisibleIndexWindow(this.enrichedData.length);
        const local = dataIndex - window.fromIndex;
        const values = this.resolveSeriesValues(priceSeries);
        if (local < 0 || local >= values.length) {
            return null;
        }
        const value = values[local];
        if (typeof value !== "number" || !Number.isFinite(value)) {
            return null;
        }
        const domain = snapshot.scaleSlotStates[priceSeries.scaleSlotId]?.domain;
        if (!domain) {
            return null;
        }
        const yScale = this.createYScaleFromDomain(domain, snapshot.plotBounds.top, snapshot.plotBounds.height, this.resolvePriceScaleForSlot(priceSeries.scaleSlotId, domain));
        const y = yScale(value);
        if (!Number.isFinite(y)) {
            return null;
        }
        return { x, y, color: this.resolveSeriesStroke(priceSeries) };
    }
    /**
     * KF-12-T08: Repaint the overlay canvas (drawing primitives + crosshair).
     * Called when DirtyFlag.OVERLAY is set. Does NOT touch the main canvas.
     */
    refreshOverlay() {
        if (!this.overlayCanvas)
            return;
        const width = Math.max(1, this.container?.clientWidth || DEFAULT_WIDTH);
        const height = Math.max(1, this.container?.clientHeight || DEFAULT_HEIGHT);
        // Same DPR backing as the main canvas (see refreshSurface).
        const dpr = this.resolveDevicePixelRatio();
        const backingWidth = Math.max(1, Math.round(width * dpr));
        const backingHeight = Math.max(1, Math.round(height * dpr));
        // Guard overlay canvas dimension changes to avoid destroying the GPU backing store
        if (this.overlayCanvas.width !== backingWidth) {
            this.overlayCanvas.width = backingWidth;
        }
        if (this.overlayCanvas.height !== backingHeight) {
            this.overlayCanvas.height = backingHeight;
        }
        const ctx = this.overlayCanvas.getContext("2d", { willReadFrequently: true });
        if (!ctx)
            return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        const overlayState = this.computeOverlayState();
        this.overlayState = overlayState;
        ctx.clearRect(0, 0, width, height);
        for (let paneIdx = 0; paneIdx < this.paneSnapshots.length; paneIdx++) {
            const snapshot = this.paneSnapshots[paneIdx];
            const { plotBounds } = snapshot;
            const isBottomPane = paneIdx === this.paneSnapshots.length - 1;
            const panePrimitives = overlayState.drawingPrimitives.filter((primitive) => primitive.paneId === snapshot.paneId);
            const clippedPrimitives = panePrimitives.filter((primitive) => primitive.clipToPane !== false);
            const unclippedPrimitives = panePrimitives.filter((primitive) => primitive.clipToPane === false);
            const effectivePatTier = this.serverPatTier ?? this.patTier;
            if (clippedPrimitives.length > 0) {
                ctx.save();
                ctx.translate(plotBounds.left, plotBounds.top);
                ctx.beginPath();
                ctx.rect(0, 0, plotBounds.width, plotBounds.height);
                ctx.clip();
                for (const primitive of clippedPrimitives) {
                    if (isPremiumDrawingBlocked(primitive.primitive.type, effectivePatTier)) {
                        continue;
                    }
                    try {
                        renderOverlayPrimitiveToCanvas(ctx, primitive.primitive, {
                            chartWidth: plotBounds.width,
                            chartHeight: plotBounds.height,
                            isSelected: primitive.selected,
                            patTier: effectivePatTier,
                        });
                    }
                    catch (e) {
                        console.error(`Failed to render clipped primitive of type ${primitive.primitive.type}:`, e);
                    }
                }
                ctx.restore();
            }
            if (unclippedPrimitives.length > 0) {
                ctx.save();
                ctx.translate(plotBounds.left, plotBounds.top);
                for (const primitive of unclippedPrimitives) {
                    if (isPremiumDrawingBlocked(primitive.primitive.type, effectivePatTier)) {
                        continue;
                    }
                    try {
                        renderOverlayPrimitiveToCanvas(ctx, primitive.primitive, {
                            chartWidth: plotBounds.width,
                            chartHeight: plotBounds.height,
                            isSelected: primitive.selected,
                            patTier: effectivePatTier,
                        });
                    }
                    catch (e) {
                        console.error(`Failed to render unclipped primitive of type ${primitive.primitive.type}:`, e);
                    }
                }
                ctx.restore();
            }
            // Snap crosshair X to follow xIndexFloat for sub-bar precision (sync-ready).
            // P2 (E3): magnet mode additionally snaps Y to the hovered-bar OHLC.
            const snappedPos = this.crosshairPos && overlayState.crosshair?.dataIndex != null
                ? (() => {
                    const projector = createPaneProjector(snapshot, this.visibleRange, "__unused-scale-slot__");
                    const floatIdx = overlayState.crosshair.xIndexFloat ?? overlayState.crosshair.dataIndex + 0.5;
                    const snappedX = projector.indexToPixelX(floatIdx);
                    if (snappedX === null) {
                        return this.crosshairPos;
                    }
                    return {
                        x: snappedX,
                        y: this.resolveMagnetSnappedY(snapshot, overlayState.crosshair.dataIndex, this.crosshairPos.y),
                    };
                })()
                : this.crosshairPos;
            this.crosshairRenderer.render(ctx, {
                plotBounds,
                position: snappedPos,
                themeMode: this.theme.mode,
                crosshairColor: this.theme.crosshairColor,
                isBottomPane,
                mode: this.crosshairMode === "dot" ? "dot" : "cross",
                beacon: snappedPos
                    ? this.resolvePriceBeacon(snapshot, snappedPos.x, overlayState.crosshair?.dataIndex)
                    : null,
            });
        }
        this.emitOverlayStateIfChanged();
    }
    computePaneScaleSlotDomains(pane) {
        const aggregates = new Map();
        for (const series of pane.series) {
            if (series.visible === false) {
                continue;
            }
            // Candlestick domain: use high/low values from enrichedData (not close scalar)
            // CT-P1b note: HA candles provably stay inside the raw OHLC envelope
            // (HAopen/HAclose are convex combos of past raw values), so the raw
            // domain is identical — no transform needed here, only at render.
            if (this.isCandlestickSeries(series)) {
                const rawDomain = this.resolveCandlestickRawDomain(this.resolveCandlestickData());
                // Skip when enrichedData is empty — overlay indicators (EMA/BB) will drive the slot domain
                if (rawDomain !== null) {
                    const current = aggregates.get(series.scaleSlotId);
                    if (!current) {
                        aggregates.set(series.scaleSlotId, { min: rawDomain[0], max: rawDomain[1], hasBarSeries: false });
                    }
                    else {
                        current.min = Math.min(current.min, rawDomain[0]);
                        current.max = Math.max(current.max, rawDomain[1]);
                    }
                }
                continue;
            }
            for (const { spec: subSeries } of this.expandSeriesIntoSubLines(series)) {
                const values = this.resolveSeriesValues(subSeries);
                if (values.length === 0) {
                    continue;
                }
                const finiteValues = values.filter((value) => Number.isFinite(value));
                if (finiteValues.length === 0) {
                    continue;
                }
                const mode = this.resolveSeriesMode(subSeries);
                // P&F: accessor chỉ trả MỘT số/bar (cạnh dẫn đầu cột) nên miền suy ra từ
                // các số đó hẹp hơn hình học vẽ ⇒ nới tại đây. Xem `pointAndFigureColumns`.
                const pnfWindow = isColumnStackMode(mode) ? this.resolveVisibleIndexWindow(this.enrichedData.length) : null;
                const domain = this.createDomainFromSeriesValues(finiteValues, isZeroFlooredMode(mode));
                if (pnfWindow) {
                    widenDomainForPointAndFigure(domain, this.enrichedData, pnfWindow.fromIndex, values.length);
                }
                const current = aggregates.get(subSeries.scaleSlotId);
                if (!current) {
                    aggregates.set(subSeries.scaleSlotId, {
                        min: domain[0],
                        max: domain[1],
                        hasBarSeries: isZeroFlooredMode(mode),
                    });
                    continue;
                }
                current.min = Math.min(current.min, domain[0]);
                current.max = Math.max(current.max, domain[1]);
                current.hasBarSeries = current.hasBarSeries || isZeroFlooredMode(mode);
            }
        }
        const domains = new Map();
        for (const slot of pane.scaleSlots) {
            const customDomain = this.customSlotDomains.get(slot.id);
            if (customDomain) {
                domains.set(slot.id, customDomain);
                continue;
            }
            const aggregate = aggregates.get(slot.id);
            if (aggregate) {
                domains.set(slot.id, this.padDomain([aggregate.min, aggregate.max], aggregate.hasBarSeries));
                continue;
            }
            if (slot.domain) {
                domains.set(slot.id, [...slot.domain]);
            }
        }
        return domains;
    }
    createDomainFromSeriesValues(values, includeZeroFloor) {
        const minValue = Math.min(...values);
        const maxValue = Math.max(...values);
        const min = includeZeroFloor ? Math.min(0, minValue) : minValue;
        return [min, maxValue];
    }
    padDomain(domain, includeZeroFloor) {
        const [rawMin, rawMax] = domain;
        const min = Math.min(rawMin, rawMax);
        const max = Math.max(rawMin, rawMax);
        const range = Math.max(1e-6, max - min);
        const paddedMin = includeZeroFloor ? min : min - range * 0.05;
        const paddedMax = max + range * 0.05;
        if (paddedMin === paddedMax) {
            const delta = Math.max(1, Math.abs(paddedMax) * 0.01);
            return [paddedMin - delta, paddedMax + delta];
        }
        return [paddedMin, paddedMax];
    }
    /** Returns true for series types rendered via CandlestickRenderer (not the scalar line/bar path) */
    isCandlestickSeries(series) {
        const t = series.type;
        return t === "Candlestick" || t === "HeikinAshi" || t === "HollowCandle" || t === "Hollow" || t === "OHLC";
    }
    /**
     * Extract OHLCV slice from enrichedData by visibleRange.
     * Returns CandlestickDatum[] with x = bar index within the slice.
     */
    resolveCandlestickData() {
        if (this.enrichedData.length === 0)
            return [];
        const { fromIndex, toIndexExclusive } = this.resolveVisibleIndexWindow(this.enrichedData.length);
        const sliced = this.enrichedData.slice(fromIndex, toIndexExclusive);
        const result = [];
        for (let i = 0; i < sliced.length; i++) {
            const d = sliced[i];
            const open = typeof d["open"] === "number" && Number.isFinite(d["open"]) ? d["open"] : null;
            const high = typeof d["high"] === "number" && Number.isFinite(d["high"]) ? d["high"] : null;
            const low = typeof d["low"] === "number" && Number.isFinite(d["low"]) ? d["low"] : null;
            const close = typeof d["close"] === "number" && Number.isFinite(d["close"]) ? d["close"] : null;
            if (open === null || high === null || low === null || close === null)
                continue;
            if (open === 0 && high === 0 && low === 0 && close === 0)
                continue;
            result.push({ x: fromIndex + i, open, high, low, close });
        }
        return result;
    }
    /** Returns raw [minLow, maxHigh] across provided OHLCV bars, or null when empty. */
    resolveCandlestickRawDomain(data) {
        if (data.length === 0)
            return null;
        let minLow = Infinity;
        let maxHigh = -Infinity;
        for (const d of data) {
            if (d.low < minLow)
                minLow = d.low;
            if (d.high > maxHigh)
                maxHigh = d.high;
        }
        return [minLow, maxHigh];
    }
    resolveCandlestickLodDomain(data) {
        const rawDomain = this.resolveCandlestickRawDomain(data);
        return rawDomain !== null ? this.padDomain(rawDomain, false) : null;
    }
    /**
     * Render a candlestick series. Called from renderPaneSeries when isCandlestickSeries is true.
     * Reads OHLCV from enrichedData (sliced by visibleRange) and delegates to CandlestickRenderer.
     */
    renderCandlestickSeriesIntoPane(ctx, snapshot, series, plotBounds, slotDomains) {
        const cacheKey = `${snapshot.paneId}:${series.id ?? series.type}:${series.scaleSlotId}`;
        const cached = this.lastCandlestickRenderCache.get(cacheKey);
        // CT-P1b: transform BEFORE LOD bucketing (bucketing raw bars then
        // transforming would corrupt the sequential HA averages).
        const rawData = this.resolveCandlestickData();
        const data = series.type === "HeikinAshi" ? toHeikinAshiCandles(rawData) : rawData;
        const rawDomain = this.resolveCandlestickRawDomain(data);
        const domain = slotDomains.get(series.scaleSlotId) ?? (rawDomain !== null ? this.padDomain(rawDomain, false) : null) ?? cached?.domain ?? null;
        if (!domain)
            return;
        const yScale = this.createYScaleFromDomain(domain, plotBounds.top, plotBounds.height, this.resolvePriceScaleForSlot(series.scaleSlotId, domain));
        // TRACE: render-time state
        const visibleCount = this.resolveVisibleBarCount();
        const barStep = plotBounds.width / visibleCount;
        const candleWidthRatio = this.resolveSeriesCandleWidthRatio(series);
        const maxAutoBarWidth = Math.max(1, barStep * 0.95);
        const barWidth = Math.min(Math.max(1, barStep * candleWidthRatio), maxAutoBarWidth, SAFE_MAX_BAR_WIDTH);
        const strokeWidth = this.resolveSeriesLineWidth(series, visibleCount, plotBounds.width);
        let lodBucketSize = 1;
        let lodData = [];
        let lodRequestKey = null;
        let fromWorker = false;
        if (data.length > 0) {
            lodBucketSize = chooseLodBucketSize(plotBounds.width, data.length, this.previousLodBucketSize);
            this.previousLodBucketSize = lodBucketSize;
            const lodResolution = this.candlestickLodWorkerLane.resolve(this.lastCandlestickRenderCache, cacheKey, data, lodBucketSize);
            lodRequestKey = lodResolution.requestKey;
            fromWorker = lodResolution.fromWorker;
            lodData = lodResolution.lodData
                ?? (lodBucketSize > 1
                    ? toCandlestickLodData(aggregateOhlc(toCandlestickLodWorkerInput(data), lodBucketSize))
                    : data);
        }
        else if (cached) {
            lodBucketSize = cached.lodBucketSize;
            lodData = cached.lodData;
        }
        if (lodData.length === 0)
            return;
        // CT-P1a: prevClose for hollow 4-state + OHLC coloring, derived post-LOD
        // so aggregated buckets carry it too (first bar keeps legacy behavior).
        for (let i = 1; i < lodData.length; i++) {
            lodData[i].prevClose = lodData[i - 1].close;
        }
        const bucketProjector = createPaneProjector(snapshot, this.visibleRange, series.scaleSlotId);
        const lodBarWidth = barWidth;
        const modeMap = { Hollow: "hollow", HollowCandle: "hollow", OHLC: "ohlc" };
        const mode = modeMap[series.type] ?? "candlestick";
        // A2: per-side candle colours from `SeriesSpec.style`. A key that is
        // absent leaves that side alone — the renderer falls back to its own
        // direction colour — so no chart that sets no style changes at all.
        const { stroke, wickStroke } = resolveCandleStyleColors(series);
        this.candlestickRenderer.render(ctx, lodData, {
            yScale,
            xAccessor: (datum) => bucketProjector.indexToPixelX(datum.x + lodBucketSize * 0.5) ?? plotBounds.left,
            barWidth: lodBarWidth,
            strokeWidth,
            mode,
            stroke,
            wickStroke,
        });
        this.lastCandlestickRenderCache.set(cacheKey, {
            lodData,
            lodBucketSize,
            domain,
            requestKey: lodRequestKey,
            lodSource: fromWorker ? "worker" : "sync",
        });
    }
    /**
     * Render mode for a series. Thin delegation to `exoticSeriesModes` (the SSOT)
     * so the geometry table cannot drift between this call site and the module
     * that owns the exotic types.
     */
    resolveSeriesMode(series) {
        return resolveSeriesRenderMode(series);
    }
    /**
     * Expand a single SeriesSpec into one or more sub-series for multi-line indicators.
     * Each entry has a resolved spec (with type pointing to the sub-line key) and a subKey label.
     * For non-multi-line types, returns a single entry with the original spec.
     *
     * Handled types:
     *   BollingerBand ΓåÆ upper / middle / lower
     *   MACD          ΓåÆ divergence bars / signal line / MACD line
     *   KDJ           ΓåÆ K / D / J lines
     */
    expandSeriesIntoSubLines(series) {
        switch (series.type) {
            case "BollingerBand":
                return [
                    { spec: { ...series, type: "BollingerBand.upper" }, subKey: "upper" },
                    { spec: { ...series }, subKey: null }, // middle (main)
                    { spec: { ...series, type: "BollingerBand.lower" }, subKey: "lower" },
                ];
            case "MACD":
                return [
                    { spec: { ...series }, subKey: null }, // divergence bars
                    {
                        spec: { ...series, type: "MACD.signal", style: { ...series.style, asArea: false } },
                        subKey: "signal",
                    },
                    {
                        spec: { ...series, type: "MACD.macd", style: { ...series.style, asArea: false } },
                        subKey: "macd",
                    },
                ];
            case "KDJ":
                return [
                    { spec: { ...series }, subKey: null }, // K (main)
                    { spec: { ...series, type: "KDJ.D" }, subKey: "d" },
                    { spec: { ...series, type: "KDJ.J" }, subKey: "j" },
                ];
            default:
                return [{ spec: series, subKey: null }];
        }
    }
    resolveSeriesValues(series) {
        const missingValueFallback = this.resolveMissingSeriesValueFallback(series);
        const isDerivedSubSeries = series.type.includes(".");
        // Priority 0: overlaySymbolId (Comparison series) — read from enriched datum
        // at native index alignment. DATA-02: valueMode decides the buffer so the
        // line, axis and tooltip share one value space.
        if (series.overlaySymbolId && this.enrichedData.length > 0) {
            const percent = series.valueMode === "percent";
            const window = this.resolveVisibleIndexWindow(this.enrichedData.length);
            const slice = this.enrichedData.slice(window.fromIndex, window.toIndexExclusive);
            const values = [];
            for (let i = 0; i < slice.length; i += 1) {
                const row = slice[i];
                const bucket = percent ? row["normalizedPrices"] : row["symbolData"];
                const entry = bucket != null && typeof bucket === "object" && !Array.isArray(bucket)
                    ? bucket[series.overlaySymbolId]
                    : undefined;
                const value = percent
                    ? entry
                    : entry != null && typeof entry === "object" && !Array.isArray(entry)
                        ? entry["close"]
                        : undefined;
                values.push(typeof value === "number" && Number.isFinite(value) ? value : missingValueFallback);
            }
            const hasFinite = values.some(Number.isFinite);
            if (hasFinite) {
                return values;
            }
        }
        // Priority 1: runtime dataSource injection — highest precedence
        // Slice by visibleRange so pan/zoom changes what is rendered.
        // IMPORTANT: anchor runtime series to global candle indices so indicator X scale
        // cannot drift when runtime series length is shorter/sparser than enrichedData.
        const runtimeSeriesData = !isDerivedSubSeries ? this.dataSource?.seriesData?.[series.id] : undefined;
        if (Array.isArray(runtimeSeriesData) && runtimeSeriesData.length > 0) {
            const windowLength = this.enrichedData.length > 0
                ? this.enrichedData.length
                : runtimeSeriesData.length;
            const runtimeWindow = this.resolveVisibleIndexWindow(windowLength);
            const values = [];
            const runtimeDataLen = runtimeSeriesData.length;
            for (let globalIndex = runtimeWindow.fromIndex; globalIndex < runtimeWindow.toIndexExclusive; globalIndex += 1) {
                // Guard: runtimeSeriesData may be shorter than enrichedData
                const value = globalIndex >= 0 && globalIndex < runtimeDataLen
                    ? runtimeSeriesData[globalIndex]
                    : undefined;
                if (typeof value === "number" && Number.isFinite(value)) {
                    values.push(value);
                    continue;
                }
                values.push(missingValueFallback);
            }
            if (values.some(Number.isFinite)) {
                return values;
            }
        }
        // Priority 2: enrichedData + params-aware accessor (KF-10) → INDICATOR_ACCESSORS fallback
        if (this.enrichedData.length > 0) {
            const window = this.resolveVisibleIndexWindow(this.enrichedData.length);
            const paramsAwareAccessor = getAccessorFor(series.type, series.params);
            const fallbackAccessor = INDICATOR_ACCESSORS[series.type];
            const accessors = [
                paramsAwareAccessor,
                paramsAwareAccessor !== fallbackAccessor ? fallbackAccessor : undefined,
            ].filter((accessor) => typeof accessor === "function");
            if (accessors.length > 0) {
                const slice = this.enrichedData.slice(window.fromIndex, window.toIndexExclusive);
                for (const accessor of accessors) {
                    const rawVals = slice.map(accessor);
                    if (rawVals.some((value) => typeof value === "number" && Number.isFinite(value))) {
                        return rawVals.map((value) => typeof value === "number" && Number.isFinite(value) ? value : missingValueFallback);
                    }
                }
            }
        }
        // Priority 3: sampleValues fallback (demo-only)
        const sampleValues = series.params?.["sampleValues"];
        if (Array.isArray(sampleValues)) {
            return sampleValues
                .map((value) => (typeof value === "number" ? value : Number.NaN))
                .filter(Number.isFinite);
        }
        return [];
    }
    resolveMissingSeriesValueFallback(series) {
        return isZeroFlooredMode(this.resolveSeriesMode(series)) ? 0 : Number.NaN;
    }
    resolveScalarOverlayLodBucketSize(pane, scaleSlotId, plotWidth) {
        if (this.enrichedData.length === 0) {
            return 1;
        }
        const sharesCandlestickLane = pane.series.some((series) => (series.visible !== false
            && series.scaleSlotId === scaleSlotId
            && this.isCandlestickSeries(series)));
        if (!sharesCandlestickLane) {
            return 1;
        }
        const window = this.resolveVisibleIndexWindow(this.enrichedData.length);
        return chooseLodBucketSize(plotWidth, Math.max(1, window.toIndexExclusive - window.fromIndex));
    }
    resolveRepresentativeBucketValue(values, bucketStart, bucketEnd, bucketSize) {
        const bucketCenter = bucketStart + bucketSize / 2;
        let bestDistance = Number.POSITIVE_INFINITY;
        let matchedValueSum = 0;
        let matchedValueCount = 0;
        for (let index = bucketStart; index < bucketEnd; index += 1) {
            const value = values[index];
            if (!Number.isFinite(value)) {
                continue;
            }
            const distance = Math.abs(index + 0.5 - bucketCenter);
            if (distance < bestDistance - 1e-9) {
                bestDistance = distance;
                matchedValueSum = value;
                matchedValueCount = 1;
                continue;
            }
            if (Math.abs(distance - bestDistance) <= 1e-9) {
                matchedValueSum += value;
                matchedValueCount += 1;
            }
        }
        return matchedValueCount > 0
            ? matchedValueSum / matchedValueCount
            : Number.NaN;
    }
    toLineAreaPoints(values, bucketProjector, fromIndex, bucketSize = 1) {
        if (values.length === 0) {
            return [];
        }
        if (bucketSize > 1) {
            const points = [];
            for (let bucketStart = 0; bucketStart < values.length; bucketStart += bucketSize) {
                const bucketEnd = Math.min(values.length, bucketStart + bucketSize);
                const value = this.resolveRepresentativeBucketValue(values, bucketStart, bucketEnd, bucketSize);
                if (!Number.isFinite(value)) {
                    continue;
                }
                const x = bucketProjector.indexToPixelX(fromIndex + bucketStart + bucketSize / 2);
                if (x === null) {
                    continue;
                }
                points.push({
                    x,
                    value,
                });
            }
            return points;
        }
        if (values.length === 1) {
            const centerX = bucketProjector.indexToPixelX(fromIndex + 0.5);
            return Number.isFinite(values[0])
                ? [{ x: centerX ?? 0, value: values[0] }]
                : [];
        }
        const points = [];
        for (let index = 0; index < values.length; index += 1) {
            const value = values[index];
            if (!Number.isFinite(value)) {
                continue;
            }
            const x = bucketProjector.indexToPixelX(fromIndex + index + 0.5);
            if (x === null) {
                continue;
            }
            points.push({
                x,
                value,
            });
        }
        return points;
    }
    toBarPoints(values, bucketProjector, fromIndex, palette, baselines) {
        const points = [];
        for (let index = 0; index < values.length; index += 1) {
            const value = values[index];
            if (!Number.isFinite(value)) {
                continue;
            }
            const x = bucketProjector.indexToPixelX(fromIndex + index + 0.5);
            if (x === null) {
                continue;
            }
            points.push({
                x,
                value,
                paletteColor: palette?.[index],
                baseline: baselines?.[index],
            });
        }
        return points;
    }
    resolveMinSpan(dataLen) {
        const xViewport = this.resolveXAxisViewport();
        // SSOT floor (D-Z13): never below MIN_VISIBLE_BARS, never above dataLen.
        const minCount = Math.max(MIN_VISIBLE_BARS, Math.ceil(xViewport.width / (SAFE_MAX_BAR_WIDTH / 0.8)));
        return Math.max(1, Math.min(dataLen, minCount));
    }
    resolveBarWidth(count, width) {
        const step = width / Math.max(1, count);
        const maxAutoBarWidth = Math.max(1, step * 0.95);
        return Math.min(Math.max(1, step * 0.8), maxAutoBarWidth, SAFE_MAX_BAR_WIDTH);
    }
    resolveVisibleBarCount() {
        const { from, to } = this.visibleRange;
        return Math.max(1, Math.round(Math.abs(to - from)));
    }
    resolveVisibleIndexWindow(maxLength = this.enrichedData.length) {
        const lower = Math.min(this.visibleRange.from, this.visibleRange.to);
        const upper = Math.max(this.visibleRange.from, this.visibleRange.to);
        const boundedLength = Math.max(0, maxLength);
        const fromIndex = Math.max(0, Math.min(boundedLength, Math.floor(lower)));
        let toIndexExclusive = Math.max(fromIndex, Math.min(boundedLength, Math.ceil(upper)));
        if (toIndexExclusive === fromIndex && boundedLength > fromIndex) {
            toIndexExclusive = Math.min(boundedLength, fromIndex + 1);
        }
        const span = Math.max(1, toIndexExclusive - fromIndex);
        return { fromIndex, toIndexExclusive, span };
    }
    createYScaleFromDomain(domain, top, height, scale) {
        const [min, max] = domain;
        const normalizedMin = Math.min(min, max);
        const normalizedMax = Math.max(min, max);
        const span = Math.max(1e-6, normalizedMax - normalizedMin);
        const normDomain = [normalizedMin, normalizedMax];
        return (value) => {
            const ratio = scaleValueToRatio(value, normDomain, scale);
            return top + height - ratio * height;
        };
    }
    resolveSeriesStroke(series) {
        return this.resolveConfiguredSeriesColor(series, "stroke") ?? "#2962ff";
    }
    resolveSeriesFill(series) {
        const fill = this.resolveConfiguredSeriesColor(series, "fill");
        if (fill) {
            return fill;
        }
        return this.resolveSeriesMode(series) === "bar"
            ? "#6BA583"
            : "rgba(41,98,255,0.2)";
    }
    resolveSeriesLineWidth(series, visibleCount, plotWidth) {
        const style = this.resolveSeriesStyle(series);
        const lineWidth = style.lineWidth;
        const baseLineWidth = typeof lineWidth === "number" && Number.isFinite(lineWidth)
            ? Math.max(1, lineWidth)
            : 2;
        const zoomScale = this.resolveZoomLineWidthScale(visibleCount, plotWidth);
        return Math.max(1, baseLineWidth * zoomScale);
    }
    resolveZoomLineWidthScale(visibleCount, plotWidth) {
        const safeCount = Math.max(1, visibleCount);
        const stepPx = plotWidth / safeCount;
        if (!Number.isFinite(stepPx) || stepPx <= 0) {
            return 1;
        }
        // Use PANEL_HEADER_HEIGHT as a reference "typical bar width" for normalizing
        const scaled = Math.sqrt(stepPx / PANEL_HEADER_HEIGHT);
        return Math.max(0.7, Math.min(1.6, scaled));
    }
    resolveSeriesCandleWidthRatio(series) {
        const raw = series.params?.["candleWidthRatio"];
        if (typeof raw !== "number" || !Number.isFinite(raw)) {
            return 0.8;
        }
        return Math.max(0.2, Math.min(1.2, raw));
    }
    resolveConfiguredSeriesColor(series, channel) {
        const style = this.resolveSeriesStyle(series);
        const direct = style[channel];
        if (typeof direct === "string" && direct.length > 0) {
            return direct;
        }
        const subColorKey = this.resolveSeriesSubColorKey(series.type);
        const subColors = style.subColors;
        if (subColorKey && subColors && typeof subColors === "object") {
            const subColor = subColors[subColorKey];
            if (typeof subColor === "string" && subColor.length > 0) {
                return subColor;
            }
        }
        const color = style.color;
        if (typeof color === "string" && color.length > 0) {
            return color;
        }
        return this.resolveDefaultSeriesSubColor(series.type, channel);
    }
    resolveDefaultSeriesSubColor(seriesType, channel) {
        if (channel !== "stroke") {
            return undefined;
        }
        switch (seriesType) {
            case "MACD.signal":
                return "#f59e0b";
            case "MACD.macd":
                return "#2962ff";
            default:
                return undefined;
        }
    }
    resolveSeriesStyle(series) {
        return series.style && typeof series.style === "object"
            ? series.style
            : {};
    }
    resolveSeriesSubColorKey(seriesType) {
        switch (seriesType) {
            case "BollingerBand":
                return "middle";
            case "BollingerBand.upper":
                return "top";
            case "BollingerBand.lower":
                return "bottom";
            case "MACD":
                return "diverge";
            case "MACD.signal":
                return "signal";
            case "MACD.macd":
                return "macd";
            case "KDJ":
                return "k";
            case "KDJ.D":
                return "d";
            case "KDJ.J":
                return "j";
            default:
                return null;
        }
    }
    resolveDefaultBarPalette(series, values) {
        if (series.type === "Volume") {
            const { fromIndex, toIndexExclusive } = this.resolveVisibleIndexWindow(this.enrichedData.length);
            const visibleBars = this.enrichedData.slice(fromIndex, toIndexExclusive);
            return values.map((_, index) => {
                const datum = visibleBars[index];
                const open = typeof datum?.open === "number" ? datum.open : Number.NaN;
                const close = typeof datum?.close === "number" ? datum.close : Number.NaN;
                return Number.isFinite(open) && Number.isFinite(close) && close < open
                    ? "rgba(242, 54, 69, 0.45)"
                    : "rgba(8, 153, 129, 0.45)";
            });
        }
        if (isZeroFlooredMode(this.resolveSeriesMode(series))) {
            return values.map((value) => value < 0 ? "#FF0000" : "#6BA583");
        }
        return undefined;
    }
    // ============================================
    // Snapshot-based freeze/thaw (ADR-0011)
    // ============================================
    /**
     * Capture current frame as an ImageBitmap for freeze/thaw visual continuity.
     * Uses OffscreenCanvas + transferToImageBitmap() for non-blocking capture.
     * Falls back to canvas.toDataURL() for Safari <16.4.
     * Returns Promise resolving to ImageBitmap or HTMLCanvasElement.
     */
    async captureSnapshot() {
        if (!this.canvas || !this.surface) {
            throw new Error("Engine not mounted - cannot capture snapshot");
        }
        // Get the current canvas dimensions (CSS pixels)
        const rect = this.canvas.getBoundingClientRect();
        const width = Math.min(Math.floor(rect.width), 1920); // Max 1920px width per ADR-0011
        const height = Math.floor(rect.height * (width / rect.width)); // Maintain aspect ratio
        // Try OffscreenCanvas first (non-blocking, zero-copy)
        if (typeof OffscreenCanvas !== "undefined" && typeof OffscreenCanvasRenderingContext2D !== "undefined") {
            try {
                const offscreenCanvas = new OffscreenCanvas(width, height);
                const offscreenCtx = offscreenCanvas.getContext("2d");
                if (offscreenCtx) {
                    // Draw the main canvas to offscreen canvas
                    offscreenCtx.drawImage(this.canvas, 0, 0, width, height);
                    // Draw overlay canvas on top if it exists
                    if (this.overlayCanvas) {
                        offscreenCtx.drawImage(this.overlayCanvas, 0, 0, width, height);
                    }
                    // Transfer to ImageBitmap (zero-copy)
                    const imageBitmap = await offscreenCanvas.transferToImageBitmap();
                    return imageBitmap;
                }
            }
            catch (e) {
                console.warn("[KernelEngine] OffscreenCanvas capture failed, falling back to toDataURL:", e);
            }
        }
        // Fallback: Use toDataURL on main thread (blocking but widely supported)
        try {
            const fallbackCanvas = document.createElement("canvas");
            fallbackCanvas.width = width;
            fallbackCanvas.height = height;
            const fallbackCtx = fallbackCanvas.getContext("2d", { willReadFrequently: true });
            if (fallbackCtx) {
                fallbackCtx.drawImage(this.canvas, 0, 0, width, height);
                if (this.overlayCanvas) {
                    fallbackCtx.drawImage(this.overlayCanvas, 0, 0, width, height);
                }
                return fallbackCanvas;
            }
        }
        catch (e) {
            console.error("[KernelEngine] Fallback snapshot capture failed:", e);
        }
        // Last resort: return a blank canvas
        const blankCanvas = document.createElement("canvas");
        blankCanvas.width = width;
        blankCanvas.height = height;
        return blankCanvas;
    }
    /**
     * Restore from a previously captured snapshot.
     * Renders snapshot to offscreen 2D canvas, then seamless swap to live WebGL.
     * Restores state: visibleRange, crosshair, drawings, chartType, timeframe.
     */
    async restoreFromSnapshot(snapshot) {
        if (!this.canvas || !this.surface) {
            throw new Error("Engine not mounted - cannot restore snapshot");
        }
        // Create a temporary canvas to hold the snapshot during transition
        const rect = this.canvas.getBoundingClientRect();
        const width = Math.floor(rect.width);
        const height = Math.floor(rect.height);
        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = width;
        tempCanvas.height = height;
        tempCanvas.style.position = "absolute";
        tempCanvas.style.top = "0";
        tempCanvas.style.left = "0";
        tempCanvas.style.width = "100%";
        tempCanvas.style.height = "100%";
        tempCanvas.style.pointerEvents = "none";
        tempCanvas.style.zIndex = "100";
        tempCanvas.style.opacity = "1";
        tempCanvas.style.transition = "opacity 50ms ease-out";
        const tempCtx = tempCanvas.getContext("2d");
        if (!tempCtx) {
            throw new Error("Failed to get 2D context for snapshot restoration");
        }
        // Draw the snapshot to the temporary canvas
        if (snapshot instanceof ImageBitmap) {
            tempCtx.drawImage(snapshot, 0, 0, width, height);
        }
        else {
            tempCtx.drawImage(snapshot, 0, 0, width, height);
        }
        // Insert the temporary canvas above the engine surface
        this.surface.appendChild(tempCanvas);
        // Wait for the next frame to ensure the snapshot is visible
        await new Promise(resolve => requestAnimationFrame(resolve));
        // Force a render to ensure WebGL context is ready
        this.scheduleRender(DirtyFlag.FULL); // All dirty flags
        // Wait for render to complete
        await new Promise(resolve => requestAnimationFrame(resolve));
        // Cross-fade: fade out snapshot, reveal live WebGL
        tempCanvas.style.opacity = "0";
        // Wait for transition to complete
        await new Promise(resolve => setTimeout(resolve, 50));
        // Remove the temporary canvas
        if (tempCanvas.parentNode) {
            tempCanvas.parentNode.removeChild(tempCanvas);
        }
    }
}
